import React, { useState, useEffect, useRef } from 'react';
import { uses12h } from '../utils/timeFormat';
import { copyText } from '../utils/clipboard';
import { a11y } from '../utils/a11yLabels';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubCalendars } from '../context/SubCalendarContext';
import ColorPicker, { PRESET_COLORS } from './ColorPicker';


import { clockPreference, setClockPreference, weekPreference, setWeekPreference } from '../utils/timeFormat';
import { notifyStatus, askNotifyPermission, NOTIFY_WORDS } from '../utils/notifyStatus';
import apiClient from '../api/client';
import { subscribeToPush } from '../hooks/usePushSubscription';
import { useT, DATE_LOCALES } from '../i18n';
import { format as dfFormat } from 'date-fns';
import TimezonePicker from './TimezonePicker';
import { ALARM_KEYS, ALERT_SOUNDS, playAlertSound, NOTIF_KEYS, getNotifSettings, SNOOZE_FOREVER } from '../utils/sounds';
import { applyTTSPrefs, langToSpeechLang, voicesReady } from '../utils/ttsUtils';
import { applyAccent, ACCENT_COLORS } from '../utils/accent';
import { TrashIcon } from './icons';
import { OkBtn, BackBtn, AddBtn, DeleteBtn, ActionRow, Spacer } from './RoundBtn';
import { TimeField } from './EventForm';
import { QUOTES, HISTORY_KEY } from './PearlCard';
import { REGEN_CONFIRM } from './ConnectCalendar';
import { BUILD_DATE, BUILD_TIME, BUILD_COMMIT } from '../buildInfo';
import { canShowPurchase, isNativeApp } from '../utils/platform';
import { buzz } from '../utils/vibrate';
import { overflowLine } from '../utils/overflow';

// The words for the clock and the first day of the week. They live here rather
// than in i18n.js for the same reason the notification block does: this is a
// panel nobody else draws, and a round trip through the shared dictionary for
// six strings buys nothing.
const CLOCK_WORDS = {
  it:      { t: 'Orologio', auto: 'Automatico', h24: '24 ore', h12: '12 ore (AM/PM)', hint: 'Automatico segue le impostazioni del telefono.', week: 'La settimana inizia' },
  en:      { t: 'Clock',    auto: 'Automatic',  h24: '24-hour', h12: '12-hour (AM/PM)', hint: 'Automatic follows your phone.', week: 'The week starts on' },
  es:      { t: 'Reloj',    auto: 'Automático', h24: '24 horas', h12: '12 horas (AM/PM)', hint: 'Automático sigue los ajustes del teléfono.', week: 'La semana empieza en' },
  de:      { t: 'Uhr',      auto: 'Automatisch', h24: '24 Stunden', h12: '12 Stunden (AM/PM)', hint: 'Automatisch folgt deinem Telefon.', week: 'Die Woche beginnt am' },
  fr:      { t: 'Horloge',  auto: 'Automatique', h24: '24 heures', h12: '12 heures (AM/PM)', hint: 'Automatique suit votre téléphone.', week: 'La semaine commence le' },
  nl:      { t: 'Klok',     auto: 'Automatisch', h24: '24 uur', h12: '12 uur (AM/PM)', hint: 'Automatisch volgt je telefoon.', week: 'De week begint op' },
  pt:      { t: 'Relógio',  auto: 'Automático', h24: '24 horas', h12: '12 horas (AM/PM)', hint: 'Automático segue o teu telemóvel.', week: 'A semana começa em' },
  'pt-BR': { t: 'Relógio',  auto: 'Automático', h24: '24 horas', h12: '12 horas (AM/PM)', hint: 'Automático segue o seu celular.', week: 'A semana começa em' },
  ru:      { t: 'Часы',     auto: 'Автоматически', h24: '24 часа', h12: '12 часов (AM/PM)', hint: 'Автоматически — как на телефоне.', week: 'Неделя начинается с' },
  pl:      { t: 'Zegar',    auto: 'Automatycznie', h24: '24 godziny', h12: '12 godzin (AM/PM)', hint: 'Automatycznie działa jak w telefonie.', week: 'Tydzień zaczyna się w' },
  ro:      { t: 'Ceas',     auto: 'Automat',    h24: '24 de ore', h12: '12 ore (AM/PM)', hint: 'Automat urmează setările telefonului.', week: 'Săptămâna începe' },
  zh:      { t: '时钟',      auto: '自动',        h24: '24 小时', h12: '12 小时（AM/PM）', hint: '自动会跟随手机的设置。', week: '一周开始于' },
  ja:      { t: '時計',      auto: '自動',        h24: '24 時間', h12: '12 時間（AM/PM）', hint: '自動は端末の設定に従います。', week: '週の始まり' },
  hi:      { t: 'घड़ी',      auto: 'स्वचालित',    h24: '24 घंटे', h12: '12 घंटे (AM/PM)', hint: 'स्वचालित आपके फ़ोन की सेटिंग का पालन करता है।', week: 'सप्ताह शुरू होता है' },
  ko:      { t: '시계',      auto: '자동',        h24: '24시간', h12: '12시간 (AM/PM)', hint: '자동은 휴대폰 설정을 따릅니다.', week: '주 시작 요일' },
  ar:      { t: 'الساعة',   auto: 'تلقائي',      h24: '24 ساعة', h12: '12 ساعة (AM/PM)', hint: 'التلقائي يتبع إعدادات هاتفك.', week: 'يبدأ الأسبوع يوم' },
};

// Lemon Squeezy hosted checkout links (Test mode). We append the user's email
// + custom data (user_id, plan) so the webhook can activate the right account.
const LS_CHECKOUT = {
  personal: 'https://memocal.lemonsqueezy.com/checkout/buy/6bbac152-5dc5-43fb-b767-db0a7b18d77a',
  premium:  'https://memocal.lemonsqueezy.com/checkout/buy/deb7fa8e-a641-4777-973a-6160b4a7dfdd',
};
function buildCheckoutUrl(plan, user) {
  const base = LS_CHECKOUT[plan];
  const p = new URLSearchParams();
  if (user?.email) p.set('checkout[email]', user.email);
  if (user?.id != null) p.set('checkout[custom][user_id]', String(user.id));
  p.set('checkout[custom][plan]', plan);
  return `${base}?${p.toString()}`;
}

const LANGUAGES = [
  { value: 'it',    label: '🇮🇹      Italiano' },
  { value: 'en-US', label: '🇺🇸      English (US)' },
  { value: 'en-GB', label: '🇬🇧      English (UK)' },
  { value: 'de',    label: '🇩🇪      Deutsch' },
  { value: 'fr',    label: '🇫🇷      Français' },
  { value: 'nl',    label: '🇳🇱      Nederlands' },
  { value: 'pt-PT', label: '🇵🇹      Português (Portugal)' },
  { value: 'pt-BR', label: '🇧🇷      Português (Brasil)' },
  { value: 'es-ES', label: '🇪🇸      Español (España)' },
  { value: 'es-AR', label: '🇦🇷      Español (Argentina)' },
  { value: 'es-MX', label: '🇲🇽      Español (México)' },
  { value: 'es-CO', label: '🇨🇴      Español (Colombia)' },
  { value: 'ru',    label: '🇷🇺      Русский' },
  { value: 'pl',    label: '🇵🇱      Polski' },
  { value: 'ro',    label: '🇷🇴      Română' },
  { value: 'zh',    label: '🇨🇳      中文' },
  { value: 'ja',    label: '🇯🇵      日本語' },
  { value: 'hi',    label: '🇮🇳      हिन्दी' },
  { value: 'ko',    label: '🇰🇷      한국어' },
  { value: 'ar',    label: '🇸🇦      العربية' },
];

// Remap old DB codes (en/pt/es) to explicit variants so the select always has a matching option
function remapLang(lang) {
  const map = { en: 'en-US', pt: 'pt-PT', es: 'es-ES' };
  return map[lang] || lang || 'it';
}

// First 8 shown inline; the rest appear when "more" is expanded
/**
 * Label colours: the same six hues as the theme (utils/accent.js), in the same
 * wheel order, but two shades instead of four — a soft one and a strong one.
 *
 * Two, not four, because these are told apart at a glance on a crowded day.
 * Four shades of the same hue side by side stop being four colours and become
 * one blurry colour, which defeats the point of a label.
 */
/**
 * Label colours: the theme's ten (utils/accent.js), in the same order, two
 * shades each — the loud one first, then the deep one.
 *
 * Loud first because that is the one most labels want. A label exists to be
 * spotted on a full day, so the eye should meet the shouting version before the
 * quiet one, and the quiet one is there for the handful of things that are
 * merely filed somewhere.
 *
 * No pastel: a label is read at a glance, on a card that is already pale, and a
 * pastel dot on a pale card is a smudge.
 */

// What gets shared when you invite somebody.
//
// Both instructions are always included, because the person sending this does
// not know what phone the person receiving it has — and "install it" that only
// works on half of phones is the kind of message that gets a shrug and no
// second attempt.
//
// The Play Store link comes first for Android, and the iPhone line points at
// the site, which is the only way MeMo installs there. That is an installation
// instruction, not a way to buy: the app itself still shows no prices.
const INVITE_TEXT = {
  it: (play, web) => `Ti consiglio MeMo — il calendario che lavora per te: aggiungi un impegno parlando, o fotografando un volantino.\n\nAndroid: ${play}\niPhone: apri ${web} con Safari, poi Condividi → "Aggiungi a Home".`,
  en: (play, web) => `I recommend MeMo — the calendar that works for you: add an appointment by speaking, or by photographing a flyer.\n\nAndroid: ${play}\niPhone: open ${web} in Safari, then Share → "Add to Home Screen".`,
  es: (play, web) => `Te recomiendo MeMo — el calendario que trabaja para ti: añade una cita hablando o fotografiando un folleto.\n\nAndroid: ${play}\niPhone: abre ${web} con Safari y luego Compartir → "Añadir a inicio".`,
  de: (play, web) => `Ich empfehle dir MeMo — der Kalender, der für dich arbeitet: Termine per Sprache oder per Foto eines Zettels eintragen.\n\nAndroid: ${play}\niPhone: ${web} in Safari öffnen, dann Teilen → "Zum Home-Bildschirm".`,
  fr: (play, web) => `Je te recommande MeMo — l'agenda qui travaille pour toi : ajoute un rendez-vous en parlant, ou en photographiant un papier.\n\nAndroid : ${play}\niPhone : ouvre ${web} dans Safari, puis Partager → "Sur l'écran d'accueil".`,
  nl: (play, web) => `Ik raad je MeMo aan — de agenda die voor je werkt: voeg een afspraak toe door te praten of een briefje te fotograferen.\n\nAndroid: ${play}\niPhone: open ${web} in Safari, dan Deel → "Zet op beginscherm".`,
  pt: (play, web) => `Recomendo-te o MeMo — o calendário que trabalha por ti: adiciona um compromisso a falar, ou fotografando um papel.\n\nAndroid: ${play}\niPhone: abre ${web} no Safari e depois Partilhar → "Adicionar ao ecrã principal".`,
  ru: (play, web) => `Советую MeMo — календарь, который работает за вас: добавляйте дела голосом или сфотографировав листовку.\n\nAndroid: ${play}\niPhone: откройте ${web} в Safari, затем «Поделиться» → «На экран «Домой»».`,
  pl: (play, web) => `Polecam MeMo — kalendarz, który pracuje za ciebie: dodaj termin głosem albo robiąc zdjęcie ulotki.\n\nAndroid: ${play}\niPhone: otwórz ${web} w Safari, potem Udostępnij → „Do ekranu początkowego".`,
  ro: (play, web) => `Îți recomand MeMo — calendarul care lucrează pentru tine: adaugi o programare vorbind sau fotografiind un pliant.\n\nAndroid: ${play}\niPhone: deschide ${web} în Safari, apoi Partajează → "Adaugă la ecranul principal".`,
  zh: (play, web) => `推荐你用 MeMo —— 会替你打理的日历：说一句话就能加日程，也可以拍下纸条自动识别。\n\nAndroid：${play}\niPhone：用 Safari 打开 ${web}，然后「分享」→「添加到主屏幕」。`,
  ja: (play, web) => `MeMo をおすすめします — あなたの代わりに働くカレンダー。話すだけで予定を追加でき、チラシを撮っても登録できます。\n\nAndroid: ${play}\niPhone: Safari で ${web} を開き、共有 →「ホーム画面に追加」。`,
  hi: (play, web) => `मैं MeMo की सलाह दूँगी — वह कैलेंडर जो आपके लिए काम करता है: बोलकर या पर्चे की फ़ोटो खींचकर काम जोड़ें।\n\nAndroid: ${play}\niPhone: Safari में ${web} खोलें, फिर शेयर → "होम स्क्रीन में जोड़ें"।`,
  ko: (play, web) => `MeMo를 추천해요 — 당신 대신 일해 주는 캘린더. 말만 해도 일정이 들어가고, 전단지를 찍어도 됩니다.\n\nAndroid: ${play}\niPhone: Safari에서 ${web} 을 열고 공유 → "홈 화면에 추가".`,
  ar: (play, web) => `أنصحك بـ MeMo — التقويم الذي يعمل من أجلك: أضف موعدًا بصوتك، أو بتصوير ورقة.\n\nAndroid: ${play}\niPhone: افتح ${web} في Safari ثم مشاركة ← "إضافة إلى الشاشة الرئيسية".`,
};
const INVITE_LABEL = {
  it: "Invita un Amico", en: "Invite a Friend", es: "Invita a un Amigo", de: "Freund Einladen",
  fr: "Inviter un Ami", nl: "Nodig een Vriend uit", pt: "Convida um Amigo", ru: "Пригласить друга",
  pl: "Zaproś Znajomego", ro: "Invită un Prieten", zh: "邀请朋友", ja: "友だちを招待",
  hi: "दोस्त को बुलाएँ", ko: "친구 초대", ar: "ادعُ صديقًا",
};
const INVITE_COPIED = {
  it: 'Testo copiato', en: 'Text copied', es: 'Texto copiado', de: 'Text kopiert',
  fr: 'Texte copié', nl: 'Tekst gekopieerd', pt: 'Texto copiado', ru: 'Текст скопирован',
  pl: 'Skopiowano', ro: 'Text copiat', zh: '已复制', ja: 'コピーしました',
  hi: 'टेक्स्ट कॉपी हुआ', ko: '복사했습니다', ar: 'تم نسخ النص',
};
// Pressing "Invite a friend" used to copy the message and say "Copied", which
// answers a question nobody asked. What somebody wants to know at that moment
// is what was copied and where to put it. So the button now opens the message
// itself, with the ways of sending it underneath.
const INVITE_HOW = {
  it: 'Ecco il messaggio. Scegli come mandarlo:',
  en: 'Here is the message. Choose how to send it:',
  es: 'Este es el mensaje. Elige cómo enviarlo:',
  de: 'Das ist die Nachricht. Wähle, wie du sie sendest:',
  fr: 'Voici le message. Choisissez comment l’envoyer :',
  nl: 'Dit is het bericht. Kies hoe je het verstuurt:',
  pt: 'Esta é a mensagem. Escolhe como a enviar:',
  ru: 'Вот сообщение. Выберите, как отправить:',
  pl: 'Oto wiadomość. Wybierz, jak ją wysłać:',
  ro: 'Acesta e mesajul. Alege cum să-l trimiți:',
  zh: '这是消息内容。选择发送方式：',
  ja: 'これがメッセージです。送り方を選んでください：',
  hi: 'यह रहा संदेश। भेजने का तरीका चुनें:',
  ko: '이것이 메시지입니다. 보낼 방법을 고르세요:',
  ar: 'هذه هي الرسالة. اختر طريقة الإرسال:',
};
const INVITE_COPY = {
  it: 'Copia', en: 'Copy', es: 'Copiar', de: 'Kopieren', fr: 'Copier', nl: 'Kopiëren',
  pt: 'Copiar', ru: 'Копировать', pl: 'Kopiuj', ro: 'Copiază', zh: '复制',
  ja: 'コピー', hi: 'कॉपी', ko: '복사', ar: 'نسخ',
};
const INVITE_SHARE = {
  it: 'Condividi', en: 'Share', es: 'Compartir', de: 'Teilen', fr: 'Partager', nl: 'Delen',
  pt: 'Partilhar', ru: 'Поделиться', pl: 'Udostępnij', ro: 'Trimite', zh: '分享',
  ja: '共有', hi: 'साझा करें', ko: '공유', ar: 'مشاركة',
};
const INVITE_SUBJECT = {
  it: 'Ti consiglio MeMo', en: 'I recommend MeMo', es: 'Te recomiendo MeMo',
  de: 'Ich empfehle dir MeMo', fr: 'Je te recommande MeMo', nl: 'Ik raad je MeMo aan',
  pt: 'Recomendo-te o MeMo', ru: 'Советую MeMo', pl: 'Polecam MeMo',
  ro: 'Îți recomand MeMo', zh: '推荐你用 MeMo', ja: 'MeMo をおすすめします',
  hi: 'मैं MeMo की सलाह देता हूँ', ko: 'MeMo를 추천합니다', ar: 'أنصحك بـ MeMo',
};

// Said when neither the share sheet nor the clipboard worked, with the address
// spelled out so there is still a way to pass MeMo on by hand.
const INVITE_FALLBACK = {
  it: 'Non sono riuscita né a condividere né a copiare. Il link è questo:',
  en: "Couldn't share or copy. Here is the link:",
  es: 'No he podido compartir ni copiar. Este es el enlace:',
  de: 'Teilen und Kopieren haben nicht geklappt. Hier ist der Link:',
  fr: "Le partage et la copie n'ont pas fonctionné. Voici le lien :",
  nl: 'Delen en kopiëren lukten niet. Dit is de link:',
  pt: 'Não consegui partilhar nem copiar. O link é este:',
  ru: 'Не удалось ни поделиться, ни скопировать. Вот ссылка:',
  pl: 'Nie udało się udostępnić ani skopiować. Oto link:',
  ro: 'Nu am putut nici partaja, nici copia. Acesta e linkul:',
  zh: '无法分享也无法复制。链接在这里：',
  ja: '共有もコピーもできませんでした。リンクはこちらです：',
  hi: 'न साझा कर सका, न कॉपी। लिंक यह है:',
  ko: '공유도 복사도 되지 않았습니다. 링크는 다음과 같습니다:',
  ar: 'تعذّرت المشاركة والنسخ. هذا هو الرابط:',
};

// What the Prova button reports. Four answers, because there are four things
// that can happen and "nothing visible" was being used for all of them.
// navigator.vibrate returns TRUE inside the installed app and then does
// nothing. So the test said "it vibrated" while the phone sat still, and
// Silvia — correctly — read that as the app lying to her. It was: I had
// taken the return value as proof, and it is not proof of anything.
//
// Until the native plugin ships, the honest answer in the installed app is
// that we asked and cannot promise. In a browser the return value IS reliable
// and the old wording stands.
const TEST_RESULT = {
  it: { both: 'Ha vibrato e ha parlato.', spokeOnly: 'Ha parlato. Questo dispositivo non vibra.', vibeOnly: 'Ha vibrato. La voce non è disponibile qui.', soon: "Ha parlato. La vibrazione l'ho chiesta, ma l'app installata non riesce ancora a farla: arriverà col prossimo aggiornamento.", neither: 'Questo dispositivo non vibra e non ha una voce disponibile.' },
  en: { both: 'It vibrated and spoke.', spokeOnly: 'It spoke. This device does not vibrate.', vibeOnly: 'It vibrated. The voice is not available here.', soon: "It spoke. The vibration was asked for, but the installed app cannot do it yet: it arrives with the next update.", neither: 'This device does not vibrate and has no voice available.' },
  es: { both: 'Ha vibrado y ha hablado.', spokeOnly: 'Ha hablado. Este dispositivo no vibra.', vibeOnly: 'Ha vibrado. La voz no está disponible aquí.', soon: "Ha hablado. He pedido la vibración, pero la app instalada aún no puede hacerla: llegará con la próxima actualización.", neither: 'Este dispositivo no vibra y no tiene voz disponible.' },
  de: { both: 'Es hat vibriert und gesprochen.', spokeOnly: 'Es hat gesprochen. Dieses Gerät vibriert nicht.', vibeOnly: 'Es hat vibriert. Die Stimme ist hier nicht verfügbar.', soon: "Es hat gesprochen. Die Vibration wurde angefordert, aber die installierte App kann sie noch nicht: sie kommt mit dem nächsten Update.", neither: 'Dieses Gerät vibriert nicht und hat keine Stimme.' },
  fr: { both: 'Il a vibré et parlé.', spokeOnly: "Il a parlé. Cet appareil ne vibre pas.", vibeOnly: "Il a vibré. La voix n'est pas disponible ici.", soon: "Il a parlé. La vibration a été demandée, mais l'app installée ne sait pas encore la faire : elle arrivera avec la prochaine mise à jour.", neither: "Cet appareil ne vibre pas et n'a pas de voix disponible." },
  nl: { both: 'Hij trilde en sprak.', spokeOnly: 'Hij sprak. Dit apparaat trilt niet.', vibeOnly: 'Hij trilde. De stem is hier niet beschikbaar.', soon: "Hij sprak. De trilling is aangevraagd, maar de geïnstalleerde app kan dat nog niet: dat komt met de volgende update.", neither: 'Dit apparaat trilt niet en heeft geen stem beschikbaar.' },
  pt: { both: 'Vibrou e falou.', spokeOnly: 'Falou. Este dispositivo não vibra.', vibeOnly: 'Vibrou. A voz não está disponível aqui.', soon: "Falou. A vibração foi pedida, mas a app instalada ainda não a consegue fazer: chega na próxima atualização.", neither: 'Este dispositivo não vibra e não tem voz disponível.' },
  ru: { both: 'Он завибрировал и произнёс.', spokeOnly: 'Он произнёс. Это устройство не вибрирует.', vibeOnly: 'Он завибрировал. Голос здесь недоступен.', soon: "Он произнёс. Вибрацию запросили, но установленное приложение пока не умеет: появится со следующим обновлением.", neither: 'Это устройство не вибрирует, и голос недоступен.' },
  pl: { both: 'Zawibrował i powiedział.', spokeOnly: 'Powiedział. To urządzenie nie wibruje.', vibeOnly: 'Zawibrował. Głos nie jest tu dostępny.', soon: "Powiedział. Wibracja została zamówiona, ale zainstalowana aplikacja jeszcze tego nie potrafi: pojawi się w kolejnej aktualizacji.", neither: 'To urządzenie nie wibruje i nie ma dostępnego głosu.' },
  ro: { both: 'A vibrat și a vorbit.', spokeOnly: 'A vorbit. Acest dispozitiv nu vibrează.', vibeOnly: 'A vibrat. Vocea nu e disponibilă aici.', soon: "A vorbit. Vibrația a fost cerută, dar aplicația instalată încă nu o poate face: vine cu următoarea actualizare.", neither: 'Acest dispozitiv nu vibrează și nu are voce disponibilă.' },
  zh: { both: '已震动并朗读。', spokeOnly: '已朗读。此设备不支持震动。', vibeOnly: '已震动。此处无法使用语音。', soon: "已朗读。震动已经请求，但安装版应用目前还做不到：下次更新就会有。", neither: '此设备不支持震动，也没有可用的语音。' },
  ja: { both: '振動して読み上げました。', spokeOnly: '読み上げました。この端末は振動しません。', vibeOnly: '振動しました。ここでは音声を使えません。', soon: "読み上げました。振動は要求しましたが、インストール版のアプリはまだ実行できません。次回の更新で使えるようになります。", neither: 'この端末は振動せず、使える音声もありません。' },
  hi: { both: 'कंपन हुआ और बोला।', spokeOnly: 'बोला। यह डिवाइस कंपन नहीं करता।', vibeOnly: 'कंपन हुआ। यहाँ आवाज़ उपलब्ध नहीं है।', soon: "बोला। कंपन के लिए कहा गया, पर इंस्टॉल किया ऐप अभी वह नहीं कर पाता: अगले अपडेट में आएगा।", neither: 'यह डिवाइस कंपन नहीं करता और कोई आवाज़ उपलब्ध नहीं है।' },
  ko: { both: '진동하고 읽었습니다.', spokeOnly: '읽었습니다. 이 기기는 진동하지 않습니다.', vibeOnly: '진동했습니다. 여기서는 음성을 쓸 수 없습니다.', soon: "읽었습니다. 진동을 요청했지만 설치된 앱은 아직 할 수 없습니다. 다음 업데이트에 추가됩니다.", neither: '이 기기는 진동하지 않고 사용할 수 있는 음성도 없습니다.' },
  ar: { both: 'اهتزّ ونطق.', spokeOnly: 'نطق. هذا الجهاز لا يهتزّ.', vibeOnly: 'اهتزّ. الصوت غير متاح هنا.', soon: "نطق. طُلب الاهتزاز، لكن التطبيق المثبَّت لا يستطيعه بعد: سيصل مع التحديث القادم.", neither: 'هذا الجهاز لا يهتزّ ولا يتوفّر فيه صوت.' },
};

// The installed app is a window onto memocal.app with no browser furniture —
// no address bar, and therefore no reload button. Everything MeMo ships arrives
// by reloading that page, so without this the only way to pick up a change was
// to close the app from the recent-apps list and open it again, which nobody
// would guess. Silvia: "dal cellulare non riesco a fare il refresh."
const RELOAD_LABEL = {
  it: 'Aggiorna MeMo', en: 'Refresh MeMo', es: 'Actualizar MeMo', de: 'MeMo aktualisieren',
  fr: 'Actualiser MeMo', nl: 'MeMo vernieuwen', pt: 'Atualizar o MeMo', ru: 'Обновить MeMo',
  pl: 'Odśwież MeMo', ro: 'Actualizează MeMo', zh: '刷新 MeMo', ja: 'MeMo を更新',
  hi: 'MeMo रीफ़्रेश करें', ko: 'MeMo 새로고침', ar: 'تحديث MeMo',
};

const PLAY_URL = 'https://play.google.com/store/apps/details?id=app.memocal';
const WEB_URL  = 'https://memocal.app';


// Emoji + optional toggle wrapper
// Recovered: pulling ColorPicker out into its own file took this with it, and
// nothing said so — the emoji button in Settings threw the moment it was
// opened. Found by turning no-undef on, not by any test we had.
const EMOJI_CATEGORIES = [
  { icon: '👤', emojis: ['👤','👥','👨','👩','🧑','👦','👧','👶','👴','👵','👨‍👩‍👧','👨‍👩‍👧‍👦','👫','🧑‍🤝‍🧑'] },
  { icon: '💼', emojis: ['💼','📚','📝','📊','💻','🏢','🎓','📅','📋','✅','🔑','📌','📎','🖊️','🗂️','📁'] },
  { icon: '🏠', emojis: ['🏠','🏡','🛒','🍽️','🛁','🛌','🪴','🧹','🍳','🔧','🪑','🛋️','🚿','🧺'] },
  { icon: '🏃', emojis: ['🏃','🏋️','🧘','⚽','🎾','🏊','🚴','💊','🏥','🩺','💪','🥗','🧗','⛷️'] },
  { icon: '✈️', emojis: ['✈️','🚗','🚌','🚂','🗺️','🌍','🏖️','⛺','🧳','🚀','🚁','🛳️','🚢','🏕️'] },
  { icon: '🎉', emojis: ['🍕','🍔','🎂','🎵','🎮','🎬','🎉','🍷','☕','🎁','🎊','🎤','🎸','🍜','🎲'] },
  { icon: '⭐', emojis: ['⭐','❤️','🔔','🎯','💡','⚡','🔥','💰','📍','🏆','🌟','💫','🐶','🐱','🌸'] },
];

function EmojiPickerPanel({ value, onChange }) {
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface)' }}>
      <div style={{ maxHeight: 200, overflowY: 'auto', padding: '8px 8px 4px' }}>
        {EMOJI_CATEGORIES.map(cat => (
          <div key={cat.icon} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: '0.6rem', color: 'var(--color-text-muted)', fontWeight: 700, letterSpacing: '0.05em', marginBottom: 4, paddingInlineStart: 2 }}>
              {cat.icon}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {cat.emojis.map(em => (
                <button key={em} type="button" onClick={() => onChange(em)}
                  style={{
                    fontSize: '1.25rem', lineHeight: 1,
                    background: value === em ? 'var(--color-primary)' : 'var(--color-card)',
                    border: `1.5px solid ${value === em ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    borderRadius: 6, padding: '3px 5px', cursor: 'pointer',
                  }}>
                  {em}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmojiPicker({ value, onChange }) {
  const [open, setOpen] = useState(Boolean(value));
  return (
    <div>
      <button type="button"
        onClick={() => { if (open) { onChange(''); setOpen(false); } else setOpen(true); }}
        style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', background: 'none', border: '1.5px solid var(--color-border)', borderRadius: 20, padding: '4px 12px', cursor: 'pointer', marginBottom: open ? 8 : 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {value
          ? <><span style={{ fontSize: '1.1rem' }}>{value}</span><span>✕</span></>
          : <>＋ emoji</>}
      </button>
      {open && <EmojiPickerPanel value={value} onChange={em => onChange(em)} />}
    </div>
  );
}

// Detect browser timezone once — used for auto-detect button and mismatch warning
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// The language map lives in utils/ttsUtils.js. A second copy lived here and
// had drifted: its fallback was 'it-IT' for anything unlisted, so a Spanish
// variant nobody had thought of — es-CL, say — was read aloud in Italian.
// The shared one falls back to the base language first.

// Short phrase spoken when the user taps "Test" next to a voice
const TEST_PHRASES = {
  it: 'Ciao! Questa è la mia voce.',
  en: 'Hello! This is my voice.',
  es: '¡Hola! Esta es mi voz.',
  de: 'Hallo! Das ist meine Stimme.',
  fr: 'Bonjour ! C\'est ma voix.',
  nl: 'Hallo! Dit is mijn stem.',
  pt: 'Olá! Esta é a minha voz.',
  ru: 'Привет! Это мой голос.',
  pl: 'Cześć! To jest mój głos.',
  ro: 'Bună! Acesta este glasul meu.',
  zh: '你好！这是我的声音。',
  ja: 'こんにちは！これが私の声です。',
  hi: 'नमस्ते! यह मेरी आवाज़ है।',
  ko: '안녕하세요! 이것이 제 목소리입니다.',
  ar: 'مرحبًا! هذا صوتي.',
};

// "Repeat each reminder in the Telegram chat" — the daily summary is unaffected.
const TG_ALERTS_L = {
  it: { label: 'Ripeti i promemoria anche su Telegram', hint: 'Il riepilogo del mattino arriva comunque.' },
  en: { label: 'Repeat reminders on Telegram too', hint: 'The morning summary arrives either way.' },
  es: { label: 'Repetir los recordatorios también en Telegram', hint: 'El resumen de la mañana llega igualmente.' },
  de: { label: 'Erinnerungen auch per Telegram wiederholen', hint: 'Die Morgenübersicht kommt weiterhin.' },
  fr: { label: 'Répéter les rappels aussi sur Telegram', hint: 'Le résumé du matin arrive de toute façon.' },
  nl: { label: 'Herinneringen ook via Telegram herhalen', hint: 'Het ochtendoverzicht komt sowieso.' },
  pt: { label: 'Repetir os lembretes também no Telegram', hint: 'O resumo da manhã chega na mesma.' },
  ru: { label: 'Дублировать напоминания в Telegram', hint: 'Утренняя сводка приходит в любом случае.' },
  pl: { label: 'Powtarzaj przypomnienia także na Telegramie', hint: 'Poranne podsumowanie przyjdzie tak czy inaczej.' },
  ro: { label: 'Repetă mementourile și pe Telegram', hint: 'Rezumatul de dimineață ajunge oricum.' },
  zh: { label: '同时在 Telegram 重复提醒', hint: '早间摘要照常发送。' },
  ja: { label: 'リマインダーをTelegramにも送る', hint: '朝のまとめは変わらず届きます。' },
  hi: { label: 'रिमाइंडर Telegram पर भी भेजें', hint: 'सुबह का सारांश वैसे भी आता रहेगा।' },
  ko: { label: '알림을 Telegram에도 보내기', hint: '아침 요약은 그대로 도착합니다.' },
  ar: { label: 'تكرار التذكيرات على تيليجرام أيضًا', hint: 'ملخّص الصباح يصل على أي حال.' },
};

// "Only when there is something to say" — the morning summary skips days with
// no events at all (own calendar AND shared groups). Applies to both channels.
const SKIP_EMPTY_L = {
  it: { label: 'Invia il riepilogo solo se ci sono eventi', hint: 'Nei giorni senza eventi non arriva nessun messaggio.' },
  en: { label: 'Send the summary only when there are events', hint: 'On days with no events, no message arrives.' },
  es: { label: 'Enviar el resumen solo si hay eventos', hint: 'Los días sin eventos no llega ningún mensaje.' },
  de: { label: 'Zusammenfassung nur senden, wenn es Termine gibt', hint: 'An Tagen ohne Termine kommt keine Nachricht.' },
  fr: { label: 'Envoyer le résumé seulement s\'il y a des événements', hint: 'Les jours sans événements, aucun message n\'arrive.' },
  nl: { label: 'Overzicht alleen sturen als er afspraken zijn', hint: 'Op dagen zonder afspraken komt er geen bericht.' },
  pt: { label: 'Enviar o resumo só quando há eventos', hint: 'Nos dias sem eventos não chega nenhuma mensagem.' },
  ru: { label: 'Отправлять сводку, только если есть события', hint: 'В дни без событий сообщение не приходит.' },
  pl: { label: 'Wysyłaj podsumowanie tylko, gdy są wydarzenia', hint: 'W dni bez wydarzeń nie przyjdzie żadna wiadomość.' },
  ro: { label: 'Trimite rezumatul doar dacă există evenimente', hint: 'În zilele fără evenimente nu sosește niciun mesaj.' },
  zh: { label: '仅在有日程时发送摘要', hint: '没有日程的日子不会收到消息。' },
  ja: { label: '予定がある日だけまとめを送る', hint: '予定のない日は何も届きません。' },
  hi: { label: 'सारांश केवल तभी भेजें जब इवेंट हों', hint: 'बिना इवेंट वाले दिनों में कोई संदेश नहीं आएगा।' },
  ko: { label: '일정이 있을 때만 요약 보내기', hint: '일정이 없는 날에는 메시지가 오지 않습니다.' },
  ar: { label: 'إرسال الملخّص فقط عند وجود أحداث', hint: 'في الأيام بلا أحداث لا تصل أي رسالة.' },
};

const WEATHER_KEY  = 'weather_permission'; // 'granted' | 'denied' | absent → unknown
const TTS_VOICE_KEY = 'tts_voice';

// Gender keyword hints — covers Windows (Microsoft TTS), iOS, Android, macOS voices
const FEMALE_HINTS = [
  // generic
  'female','woman','girl',
  // Italian — Windows: Elsa; iOS: Alice; macOS: Alice
  'alice','elsa','silvia','federica','anna','lucia','sara','elena','giulia',
  'francesca','chiara','gloria','paola','isabella','milena',
  // Spanish — Windows: Helena, Sabina; neural: Elvira, Abril
  'helena','sabina','elvira','abril','luciana','joana','ines','paulina',
  // French — Windows: Hortense; neural: Denise
  'hortense','denise','amelie','lea',
  // German — Windows: Hedda; neural: Katja, Amala
  'hedda','katja','amala',
  // English — Windows: Zira, Hazel; iOS: Samantha, Kate, Moira; neural: Aria, Jenny
  'zira','hazel','samantha','karen','moira','tessa','kate','fiona','susan',
  'allison','ava','sarah','nora','aria','jenny','emma','sonia','libby',
  // Portuguese — Windows: Helia; neural: Francisca, Raquel
  'helia','francisca','raquel','vitoria','heloisa',
  // Dutch — neural: Fenna, Colette
  'fenna','colette','hanna',
  // Russian — Windows: Irina; neural: Svetlana
  'irina','svetlana','oxana',
  // Polish — Windows: Paulina; neural: Agnieszka, Zofia
  'zosia','agnieszka','zofia',
  // Romanian — neural: Alina
  'alina',
  // Japanese/Chinese/other
  'kyoko','meijia','xiaoyan','xiaoxiao','carmit','damayanti','veena','sofia','sophia',
  'victoria','marie','lisa',
];

const MALE_HINTS = [
  // generic
  'male','man','guy',
  // Italian — Windows: Cosimo; iOS: Luca; neural: Giuseppe, Diego
  'luca','cosimo','giorgio','marco','mirko','fabio','roberto','matteo','giovanni',
  'giuseppe','diego',
  // Spanish — Windows: Pablo, Raul; neural: Alvaro, Jorge
  'pablo','raul','alvaro','jorge','alex',
  // French — Windows: Paul; neural: Henri
  'paul','henri','pierre',
  // German — Windows: Stefan; neural: Conrad, Killian
  'stefan','konrad','killian',
  // English — Windows: David, Mark, Sean; iOS: Daniel; neural: Guy, Eric, Ryan
  'david','mark','sean','daniel','thomas','george','james','fred','tom','oliver',
  'reed','eric','ryan','tony','andrew',
  // Portuguese — Windows: Daniel, Helder; neural: Fabio, Antonio
  'helder','antonio',
  // Dutch — Windows: Frank; neural: Maarten
  'frank','maarten',
  // Russian — Windows: Pavel; neural: Dmitry
  'pavel','dmitry','yuri',
  // Polish — Windows: Adam; neural: Marek
  'adam','marek',
  // Romanian — neural: Andrei
  'andrei',
  // Other
  'albert','viktor','martin','henrik','pascal','xander','eddy','otoya','sinji','ravi',
];

const TTS_GENDER_KEY = 'tts_gender';
const TTS_RATE_KEY   = 'tts_rate';

function detectGender(voice) {
  const n = voice.name.toLowerCase();
  if (FEMALE_HINTS.some(k => n.includes(k))) return 'female';
  if (MALE_HINTS.some(k => n.includes(k)))   return 'male';
  return null;
}

/** Lists all voices for the current language — user picks by name, then adjusts pitch separately */
const NO_TTS = {
  it: 'Su questo telefono non si può scegliere la voce. MeMo legge lo stesso, con la propria.',
  en: "This phone can't offer a choice of voice. MeMo still reads aloud, in its own.",
  es: 'En este teléfono no se puede elegir la voz. MeMo lee igualmente, con la suya.',
  de: 'Auf diesem Telefon lässt sich die Stimme nicht wählen. MeMo liest trotzdem vor, mit der eigenen.',
  fr: "Ce téléphone ne permet pas de choisir la voix. MeMo lit quand même, avec la sienne.",
  nl: 'Op deze telefoon kun je de stem niet kiezen. MeMo leest toch voor, met de eigen stem.',
  pt: 'Neste telemóvel não se pode escolher a voz. O MeMo lê à mesma, com a dele.',
  ru: 'На этом телефоне голос выбрать нельзя. MeMo всё равно читает вслух — своим.',
  pl: 'Na tym telefonie nie można wybrać głosu. MeMo i tak czyta — własnym.',
  ro: 'Pe acest telefon nu se poate alege vocea. MeMo citește oricum, cu a lui.',
  zh: '这台手机无法选择语音。MeMo 仍会用自己的声音朗读。',
  ja: 'この端末では声を選べません。MeMo は自分の声で読み上げます。',
  hi: 'इस फ़ोन पर आवाज़ नहीं चुनी जा सकती। MeMo फिर भी अपनी आवाज़ में पढ़ता है।',
  ko: '이 기기에서는 목소리를 고를 수 없습니다. MeMo는 자기 목소리로 계속 읽어 줍니다.',
  ar: 'لا يمكن اختيار الصوت على هذا الهاتف. سيقرأ MeMo بصوته على أي حال.',
};

function VoicePicker({ lang, T }) {
  const speechLang = langToSpeechLang(lang);
  const baseLang   = speechLang.split('-')[0];

  const [allVoices,   setAllVoices]   = useState([]);
  const [selectedURI, setSelectedURI] = useState(() => localStorage.getItem(TTS_VOICE_KEY) || '');
  // Pitch control — 'male' | 'neutral' | 'female' — maps directly to tts_gender in localStorage
  const [selectedPitch, setSelectedPitch] = useState(() => {
    const g = localStorage.getItem(TTS_GENDER_KEY);
    return g === 'male' || g === 'female' ? g : 'neutral';
  });
  const [testing, setTesting] = useState(false);
  const testRef = useRef(null);
  // Reading speed — 'slow' | 'normal' | 'fast' → stored as a numeric tts_rate
  const RATE_VALUES = { slow: 0.75, normal: 0.95, fast: 1.25 };
  const [selectedSpeed, setSelectedSpeed] = useState(() => {
    const r = parseFloat(localStorage.getItem(TTS_RATE_KEY));
    if (r && r <= 0.8) return 'slow';
    if (r && r >= 1.15) return 'fast';
    return 'normal';
  });

  function handleSpeedChange(key) {
    setSelectedSpeed(key);
    if (key === 'normal') localStorage.removeItem(TTS_RATE_KEY);
    else localStorage.setItem(TTS_RATE_KEY, String(RATE_VALUES[key]));
  }

  useEffect(() => {
    function loadVoices() {
      setAllVoices(window.speechSynthesis?.getVoices() || []);
    }
    loadVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', loadVoices);
    return () => {
      window.speechSynthesis?.removeEventListener('voiceschanged', loadVoices);
      clearTimeout(testRef.current); // don't leave the test timer running after unmount
    };
  }, []);

  // A browser with no speech engine used to make this whole section vanish —
  // no picker, no sentence, nothing. That is the case in the installed app's
  // WebView, which does not expose speechSynthesis at all. Reading events
  // aloud still works there, through MeMo's own voice on the server; it is
  // only the CHOICE of voice that this device cannot offer. Say that, rather
  // than leaving somebody looking at an empty space.
  if (!window.speechSynthesis) {
    return (
      <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.45 }}>
        {NO_TTS[String(lang || 'it').split('-')[0]] || NO_TTS.en}
      </p>
    );
  }

  const langVoices = allVoices.filter(v => v.lang.split('-')[0] === baseLang);

  function selectVoice(uri) {
    setSelectedURI(uri);
    localStorage.setItem(TTS_VOICE_KEY, uri);
    // Pitch is controlled separately — don't auto-override it here
  }

  function handlePitchChange(key) {
    setSelectedPitch(key);
    if (key === 'neutral') localStorage.removeItem(TTS_GENDER_KEY);
    else localStorage.setItem(TTS_GENDER_KEY, key);
  }

  async function handleTest() {
    window.speechSynthesis.cancel();
    clearTimeout(testRef.current);
    await voicesReady();          // same reason as the Prova button above
    const phrase = TEST_PHRASES[baseLang] || TEST_PHRASES.en;
    const utt    = new SpeechSynthesisUtterance(phrase);
    utt.lang     = speechLang;
    applyTTSPrefs(utt); // voice + pitch + chosen speed
    setTesting(true);
    utt.onend    = () => setTesting(false);
    utt.onerror  = () => setTesting(false);
    testRef.current = setTimeout(() => setTesting(false), 8000);
    window.speechSynthesis.speak(utt);
  }

  if (langVoices.length === 0) {
    return (
      <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: 0 }}>
        {T.settings.voiceNoMatch}
      </p>
    );
  }

  const PITCH_OPTIONS = [
    { key: 'male',    label: '♂ ' + (T.settings.voicePitchLow    || 'Grave')   },
    { key: 'neutral', label: '— ' + (T.settings.voicePitchMid    || 'Normale') },
    { key: 'female',  label: '♀ ' + (T.settings.voicePitchHigh   || 'Acuta')   },
  ];

  return (
    <div>
      {/* Voice list — gender badge shown next to name for quick identification */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {langVoices.map(voice => {
          const gender = detectGender(voice);
          return (
            <button
              key={voice.voiceURI}
              type="button"
              className={`subcal-picker-btn${selectedURI === voice.voiceURI ? ' selected' : ''}`}
              onClick={() => selectVoice(voice.voiceURI)}
            >
              {voice.name}
              {gender === 'female' && <span style={{ opacity: 0.65, marginInlineStart: 3 }}>♀</span>}
              {gender === 'male'   && <span style={{ opacity: 0.65, marginInlineStart: 3 }}>♂</span>}
            </button>
          );
        })}
      </div>

      {/* Pitch / tone control — works even if only female voices are available */}
      <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginBottom: 6 }}>
        {T.settings.voicePitch || 'Tonalità'}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {PITCH_OPTIONS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`subcal-picker-btn${selectedPitch === key ? ' selected' : ''}`}
            onClick={() => handlePitchChange(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Reading speed control */}
      <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginBottom: 6 }}>
        {T.settings.voiceSpeed || 'Velocità'}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[
          { key: 'slow',   label: T.settings.voiceSlow   || 'Lenta' },
          { key: 'normal', label: T.settings.voicePitchMid || 'Normale' },
          { key: 'fast',   label: T.settings.voiceFast   || 'Veloce' },
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`subcal-picker-btn${selectedSpeed === key ? ' selected' : ''}`}
            onClick={() => handleSpeedChange(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Test button */}
      {selectedURI && (
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={handleTest}
          disabled={testing}
        >
          {testing ? '…' : T.settings.voiceTest}
        </button>
      )}
    </div>
  );
}

// Shared width so "Scarica .ics", "Aggiungi etichetta" and "Meteo" line up.
const ALIGN_BTN_WIDTH = 168;

// Black Apple logo. Lives out here because two different components draw it:
// the subscription panel and the Settings button that opens it. Inside one of
// them it was invisible to the other, and the whole screen fell over.
const AppleGlyph = ({ size = 20, color = '#fff' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={color} aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

function ExportSection({ T, lang, mode }) {
  const [exporting, setExporting] = React.useState(false);
  const [exportErr, setExportErr] = React.useState(false);
  const [feed, setFeed] = React.useState(null);   // { https, webcal }
  const [copied, setCopied] = React.useState(false);
  const [regenerating, setRegenerating] = React.useState(false);

  React.useEffect(() => {
    apiClient.get('/events/ics-feed').then(r => setFeed(r.data)).catch(() => {});
  }, []);

  function copyFeed() {
    if (!feed?.https) return;
    try {
      navigator.clipboard.writeText(feed.https);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  }

  function regenerateFeed() {
    if (regenerating) return;
    // Same confirmation as ConnectCalendar: regenerating breaks already-connected calendars.
    const msg = REGEN_CONFIRM[(lang || 'it').split('-')[0]] || REGEN_CONFIRM.en;
    if (!window.confirm(msg)) return;
    setRegenerating(true);
    apiClient.post('/events/ics-feed/regenerate')
      .then(r => setFeed(r.data))
      .catch(() => {})
      .finally(() => setRegenerating(false));
  }

  async function handleExport() {
    setExporting(true);
    setExportErr(false);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await apiClient.get('/events/export', {
        responseType: 'blob',
        params: { tz },
      });
      const url  = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href     = url;
      link.download = 'memo-calendar.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(true);
    } finally {
      setExporting(false);
    }
  }

  // Localized "Connect Apple" guide (kept inline like the notifications block so
  // no i18n round-trip is needed; falls back to English).
  const base = (lang || 'it').split('-')[0];
  const GUIDE = {
    it: { btn: 'Collega calendario Apple', steps: 'Sul tuo iPhone: 1) tocca «Collega calendario Apple» qui sopra, 2) tocca «Iscriviti».\n\nScrivi i tuoi impegni in MeMo: si sincronizzano automaticamente nel calendario di Apple entro un’ora (entra in Apple e trascina giù la schermata per aggiornare subito). Nel calendario Apple si leggono gli eventi inseriti da MeMo e solo da MeMo puoi modificarli o eliminarli.', google: 'Usi Google o Outlook? Non ti serve: quei calendari sono già sincronizzati con MeMo, nei due sensi.', backup: 'Scarica una copia (backup)', backupDesc: 'Un file dei tuoi eventi da salvare o importare una volta sola. Non si aggiorna da solo.' },
    en: { btn: 'Connect Apple Calendar', steps: 'On your iPhone: 1) tap “Connect Apple Calendar” above, 2) tap “Subscribe”.\n\nWrite your plans in MeMo: they sync into Apple Calendar by themselves within an hour (open Apple Calendar and pull down to refresh right away). In Apple Calendar you can read the events MeMo put there, and only in MeMo can you change or delete them.', google: 'Use Google or Outlook? You do not need this: those calendars are already synced with MeMo, both ways.', backup: 'Download a copy (backup)', backupDesc: 'A file of your events to save or import once. It does not update by itself.' },
    es: { btn: 'Conectar calendario Apple', steps: 'En tu iPhone: 1) toca «Conectar calendario Apple» arriba, 2) toca «Suscribirse».\n\nEscribe tus planes en MeMo: se sincronizan solos con el Calendario de Apple en menos de una hora (abre Apple y desliza hacia abajo para actualizar al momento). En el Calendario de Apple se leen los eventos que ha puesto MeMo, y solo desde MeMo puedes modificarlos o eliminarlos.', google: '¿Usas Google u Outlook? No te hace falta: esos calendarios ya están sincronizados con MeMo, en los dos sentidos.', backup: 'Descargar una copia (backup)', backupDesc: 'Un archivo de tus eventos para guardar o importar una vez. No se actualiza solo.' },
    de: { btn: 'Apple Kalender verbinden', steps: 'Auf dem iPhone: 1) oben auf „Apple Kalender verbinden“ tippen, 2) auf „Abonnieren“ tippen.\n\nTrag deine Termine in MeMo ein: sie erscheinen innerhalb einer Stunde von selbst im Apple Kalender (öffne Apple und zieh nach unten, um sofort zu aktualisieren). Im Apple Kalender liest du die Termine, die MeMo dort eingetragen hat, und nur in MeMo kannst du sie ändern oder löschen.', google: 'Nutzt du Google oder Outlook? Dann brauchst du das nicht: diese Kalender sind bereits mit MeMo synchronisiert, in beide Richtungen.', backup: 'Kopie herunterladen (Backup)', backupDesc: 'Eine Datei deiner Termine zum Speichern oder einmaligen Importieren. Sie aktualisiert sich nicht selbst.' },
    fr: { btn: 'Connecter le calendrier Apple', steps: 'Sur votre iPhone : 1) touchez « Connecter le calendrier Apple » ci-dessus, 2) touchez « S’abonner ».\n\nNotez vos rendez-vous dans MeMo : ils se synchronisent tout seuls dans le Calendrier Apple en moins d’une heure (ouvrez Apple et tirez vers le bas pour actualiser tout de suite). Dans le Calendrier Apple vous lisez les événements que MeMo y a mis, et c’est seulement dans MeMo que vous pouvez les modifier ou les supprimer.', google: 'Vous utilisez Google ou Outlook ? Inutile : ces agendas sont déjà synchronisés avec MeMo, dans les deux sens.', backup: 'Télécharger une copie (sauvegarde)', backupDesc: 'Un fichier de vos événements à enregistrer ou importer une seule fois. Il ne se met pas à jour tout seul.' },
    nl: { btn: 'Apple Agenda koppelen', steps: 'Op je iPhone: 1) tik hierboven op “Apple Agenda koppelen”, 2) tik op “Abonneren”.\n\nZet je afspraken in MeMo: ze synchroniseren binnen een uur vanzelf naar Apple Agenda (open Apple en trek omlaag om meteen te vernieuwen). In Apple Agenda lees je de afspraken die MeMo daar heeft gezet, en alleen in MeMo kun je ze wijzigen of verwijderen.', google: 'Gebruik je Google of Outlook? Dan hoeft dit niet: die agenda’s zijn al met MeMo gesynchroniseerd, in beide richtingen.', backup: 'Kopie downloaden (back-up)', backupDesc: 'Een bestand met je afspraken om te bewaren of één keer te importeren. Het werkt zichzelf niet bij.' },
    pt: { btn: 'Ligar calendário Apple', steps: 'No teu iPhone: 1) toca em «Ligar calendário Apple» acima, 2) toca em «Subscrever».\n\nEscreve os teus compromissos no MeMo: sincronizam-se sozinhos no Calendário Apple dentro de uma hora (abre a Apple e puxa para baixo para atualizar já). No Calendário Apple lês os eventos que o MeMo lá pôs, e só no MeMo os podes alterar ou apagar.', google: 'Usas Google ou Outlook? Não precisas: esses calendários já estão sincronizados com o MeMo, nos dois sentidos.', backup: 'Descarregar uma cópia (backup)', backupDesc: 'Um ficheiro dos teus eventos para guardar ou importar uma só vez. Não se atualiza sozinho.' },
    ru: { btn: 'Подключить Календарь Apple', steps: 'На iPhone: 1) нажмите «Подключить Календарь Apple» выше, 2) нажмите «Подписаться».\n\nЗаписывайте дела в MeMo: они сами появятся в Календаре Apple в течение часа (откройте Apple и потяните вниз, чтобы обновить сразу). В Календаре Apple вы читаете события, добавленные MeMo, а изменить или удалить их можно только в MeMo.', google: 'Пользуетесь Google или Outlook? Это не нужно: те календари уже синхронизированы с MeMo, в обе стороны.', backup: 'Скачать копию (резервную)', backupDesc: 'Файл с вашими событиями, чтобы сохранить или импортировать один раз. Сам он не обновляется.' },
    pl: { btn: 'Połącz kalendarz Apple', steps: 'Na iPhonie: 1) dotknij „Połącz kalendarz Apple“ powyżej, 2) dotknij „Subskrybuj“.\n\nZapisuj plany w MeMo: same zsynchronizują się z Kalendarzem Apple w ciągu godziny (otwórz Apple i pociągnij w dół, aby odświeżyć od razu). W Kalendarzu Apple czytasz wydarzenia dodane przez MeMo, a zmienić je lub usunąć możesz tylko w MeMo.', google: 'Używasz Google lub Outlooka? Nie potrzebujesz tego: te kalendarze są już zsynchronizowane z MeMo, w obie strony.', backup: 'Pobierz kopię (backup)', backupDesc: 'Plik z Twoimi wydarzeniami do zapisania lub jednorazowego zaimportowania. Nie aktualizuje się sam.' },
    ro: { btn: 'Conectează calendarul Apple', steps: 'Pe iPhone: 1) atinge «Conectează calendarul Apple» de mai sus, 2) atinge «Abonează-te».\n\nScrie-ți programul în MeMo: se sincronizează singur în Calendarul Apple în mai puțin de o oră (deschide Apple și trage în jos ca să actualizezi imediat). În Calendarul Apple citești evenimentele puse de MeMo, iar doar din MeMo le poți modifica sau șterge.', google: 'Folosești Google sau Outlook? Nu îți trebuie: acele calendare sunt deja sincronizate cu MeMo, în ambele sensuri.', backup: 'Descarcă o copie (backup)', backupDesc: 'Un fișier cu evenimentele tale, pentru a-l salva sau importa o singură dată. Nu se actualizează singur.' },
    zh: { btn: '连接 Apple 日历', steps: '在 iPhone 上：1) 点按上方「连接 Apple 日历」，2) 点按「订阅」。\n\n把安排写在 MeMo 里：它们会在一小时内自动同步到 Apple 日历（打开 Apple 日历下拉即可立刻刷新）。在 Apple 日历里可以查看 MeMo 添加的日程，修改或删除只能在 MeMo 中进行。', google: '使用 Google 或 Outlook？不需要这一步：那些日历已经和 MeMo 双向同步了。', backup: '下载副本（备份）', backupDesc: '一个包含你事件的文件，可保存或一次性导入。它不会自动更新。' },
    ja: { btn: 'Appleカレンダーと連携', steps: 'iPhoneで：1) 上の「Appleカレンダーと連携」をタップ、2)「照会」をタップ。\n\n予定はMeMoに書いてください。1時間以内に自動でAppleカレンダーへ同期されます（Appleカレンダーを開いて下に引けばすぐ更新できます）。AppleカレンダーではMeMoが入れた予定を読むことができ、変更や削除はMeMoからのみ行えます。', google: 'GoogleやOutlookをお使いですか？これは不要です。そのカレンダーはすでにMeMoと双方向で同期しています。', backup: 'コピーをダウンロード（バックアップ）', backupDesc: '予定を保存したり一度だけ取り込むためのファイルです。自動更新はされません。' },
    hi: { btn: 'Apple कैलेंडर जोड़ें', steps: 'अपने iPhone पर: 1) ऊपर «Apple कैलेंडर जोड़ें» दबाएँ, 2) «सब्सक्राइब» दबाएँ।\n\nअपने काम MeMo में लिखें: वे एक घंटे के भीतर अपने आप Apple Calendar में सिंक हो जाते हैं (Apple खोलकर नीचे खींचें तो तुरंत अपडेट हो जाएगा)। Apple Calendar में MeMo के डाले हुए इवेंट पढ़ सकते हैं, और उन्हें बदलना या हटाना सिर्फ़ MeMo से हो सकता है।', google: 'Google या Outlook इस्तेमाल करते हैं? इसकी ज़रूरत नहीं: वे कैलेंडर पहले से ही MeMo के साथ दोनों तरफ़ सिंक हैं।', backup: 'एक प्रति डाउनलोड करें (बैकअप)', backupDesc: 'आपके इवेंट की एक फ़ाइल, सहेजने या एक बार इम्पोर्ट करने के लिए। यह खुद अपडेट नहीं होती।' },
    ko: { btn: 'Apple 캘린더 연결', steps: 'iPhone에서: 1) 위의 ‘Apple 캘린더 연결’을 누르고, 2) ‘구독’을 누르세요.\n\n일정은 MeMo에 적으세요. 한 시간 안에 Apple 캘린더로 저절로 동기화됩니다(Apple 캘린더를 열고 아래로 당기면 바로 새로고침됩니다). Apple 캘린더에서는 MeMo가 넣은 일정을 읽을 수 있고, 수정과 삭제는 MeMo에서만 할 수 있습니다.', google: 'Google이나 Outlook을 사용하시나요? 이 과정은 필요 없습니다. 그 캘린더는 이미 MeMo와 양방향으로 동기화되어 있습니다.', backup: '사본 다운로드 (백업)', backupDesc: '일정을 저장하거나 한 번만 가져오기 위한 파일입니다. 자동으로 업데이트되지 않습니다.' },
    ar: { btn: 'ربط تقويم Apple', steps: 'على الآيفون: 1) اضغط «ربط تقويم Apple» بالأعلى، 2) اضغط «اشتراك».\n\nاكتب مواعيدك في MeMo: تتزامن وحدها مع تقويم Apple خلال ساعة (افتح Apple واسحب للأسفل للتحديث فورًا). في تقويم Apple تقرأ الأحداث التي أضافها MeMo، ولا يمكن تعديلها أو حذفها إلا من MeMo.', google: 'تستخدم Google أو Outlook؟ لا تحتاج إلى ذلك: تلك التقاويم متزامنة بالفعل مع MeMo في الاتجاهين.', backup: 'تنزيل نسخة (احتياطية)', backupDesc: 'ملف بأحداثك لحفظه أو استيراده مرة واحدة. لا يتحدّث تلقائيًا.' },
  };
  const G = GUIDE[base] || GUIDE.en;


  // ── .ics backup mode: just the one-time download ──
  if (mode === 'ics') {
    return (
      <div style={{ padding: '4px 0' }}>
        <button type="button" onClick={handleExport} disabled={exporting}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: '2px solid var(--color-primary)', background: 'var(--color-card)', color: 'var(--color-primary)', fontWeight: 700, fontSize: '0.85rem', cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.6 : 1 }}>
          ⬇️ {exporting ? T.settings.exporting : G.backup}
        </button>
        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.45, marginTop: 8 }}>{G.backupDesc}</div>
        {exportErr && (
          <div style={{ color: '#c0392b', fontSize: '0.78rem', marginTop: 8 }}>
            {T.today?.error || 'Error — try again.'}
          </div>
        )}
      </div>
    );
  }

  // ── Apple mode: only the Apple Calendar subscription ──
  return (
    <div style={{ padding: '4px 0' }}>
      {feed?.https && (
        <div>
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text)', lineHeight: 1.45, marginBottom: 12 }}>
            {T.settings.feedDesc}
          </div>

          {/* One-tap Connect-Apple button (webcal subscribe) — black Apple button */}
          <a href={feed.webcal}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', boxSizing: 'border-box', padding: '14px 16px', borderRadius: 'var(--radius-sm)', background: '#111', color: '#fff', fontWeight: 800, fontSize: '1rem', textDecoration: 'none', marginBottom: 12 }}>
            <AppleGlyph size={20} color="#fff" /> {G.btn}
          </a>

          {/* 2-tap mini guide */}
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text)', lineHeight: 1.5, whiteSpace: 'pre-line', background: 'var(--color-surface)', border: '1.5px solid var(--color-border)', borderRadius: 10, padding: '10px 12px' }}>
            {G.steps}
          </div>

          {/* This line existed in fifteen languages and was drawn in none of
              them. It is the one that stops somebody doing real damage: a
              Google or Outlook user who subscribes to this feed gets every one
              of their own events a second time, because that calendar is
              already synced with MeMo both ways. */}
          <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: 1.45, margin: '10px 0 0' }}>
            {G.google}
          </p>

          {/* Fallback: copyable link (if the webcal button doesn't open Calendar,
              add the subscription manually with this address). */}
          {T.settings.feedHint && (
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.4, marginTop: 14, marginBottom: 6 }}>{T.settings.feedHint}</div>
          )}
          <input readOnly value={feed.https} onFocus={(e) => e.target.select()}
            style={{ width: '100%', boxSizing: 'border-box', border: '2px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', fontSize: '0.72rem', color: 'var(--color-text)', background: 'var(--color-card)', marginBottom: 8 }} />
          <button type="button" onClick={copyFeed}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '2px solid var(--color-primary)', background: 'var(--color-card)', color: 'var(--color-primary)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
            {copied ? T.settings.feedCopied : ('🔗 ' + T.settings.feedCopy)}
          </button>
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { subCalendars, createSubCalendar, updateSubCalendar, deleteSubCalendar, setOwnColour } = useSubCalendars();
  // Waiting on the server, so two taps in a row cannot make two labels.
  const [ownBusy, setOwnBusy] = useState(false);
  const [ownPicking, setOwnPicking] = useState(false);
  // 'auto' | '12' | '24'. Kept in state as well as in storage so the three
  // buttons redraw the moment one is pressed.
  const [clockPref, setClockPref] = useState(() => clockPreference());
  // 'auto' | 'sun' | 'mon'. Automatic asks the phone, exactly as the clock
  // does — an American now gets a Sunday week without having to find this.
  const [weekPref, setWeekPref] = useState(() => weekPreference());

  // New sub-calendar form
  // No colour chosen in advance. Silvia: "vorrei che l'utente potesse
  // scegliere il suo colore e che non fosse assegnato dall'app, chi usa i
  // colori è metodico." A preselected swatch is a decision made for somebody
  // who did not ask — and it is invisible: you do not notice a choice that has
  // already been made, you only notice it later, on the wrong label.
  const [newSC, setNewSC] = useState({ name: '', emoji: '', color: '' });
  const [addingSubCal, setAddingSubCal] = useState(false);
  const [editingSCId, setEditingSCId] = useState(null);
  const [editingSCData, setEditingSCData] = useState({});
  // Label save/delete failures were silent (no try/catch): the tap seemed to do
  // nothing when the request failed. Now surfaced as a small inline message.
  const [subCalError, setSubCalError] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountErr, setDeleteAccountErr] = useState('');
  const [aiUsage, setAiUsage] = useState(null); // { count, limit }
  const [bigLevel, setBigLevel] = useState(() => { try { return parseInt(localStorage.getItem('memo_bigtext'), 10) || 0; } catch (_) { return 0; } });
  const [accent, setAccent] = useState(() => { try { return localStorage.getItem('memo_accent') || ''; } catch (_) { return ''; } });
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const [a11yMenuOpen,  setA11yMenuOpen]  = useState(false);
  const [voiceMenuOpen, setVoiceMenuOpen] = useState(false);
  const [notifMenuOpen, setNotifMenuOpen] = useState(false);

  // Moved below notifMenuOpen on purpose: it reads that state, and an effect
  // that mentions a const declared further down throws before the page has
  // drawn a single pixel. The same fault that took Settings down last week.
  // Whether a reminder can actually appear on this phone. Asked once when the
  // notifications panel is opened, and again after the person taps to allow it.
  const [notifState, setNotifState] = useState(null);
  useEffect(() => {
    if (!notifMenuOpen) return;
    let gone = false;
    notifyStatus().then(v => { if (!gone) setNotifState(v); });
    return () => { gone = true; };
  }, [notifMenuOpen]);
  const [usageMenuOpen, setUsageMenuOpen] = useState(false);
  const [weatherMenuOpen, setWeatherMenuOpen] = useState(false);
  const [labelMenuOpen, setLabelMenuOpen] = useState(false);
  const [summaryMenuOpen, setSummaryMenuOpen] = useState(false);
  // Deep-link from a limit message (?upgrade=1) → open the Utilizzo/plans panel.
  useEffect(() => {
    if (/[?&]upgrade=1/.test(location.search || '')) {
      setUsageMenuOpen(true);
      setTimeout(() => { try { window.scrollTo({ top: document.body.scrollHeight * 0.45, behavior: 'smooth' }); } catch (_) {} }, 150);
    }
  }, [location.search]);

  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [tzMenuOpen, setTzMenuOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [appleMenuOpen, setAppleMenuOpen] = useState(false);
  const [icsMenuOpen, setIcsMenuOpen] = useState(false);
  const [supportMenuOpen, setSupportMenuOpen] = useState(false);
  const [deleteMenuOpen, setDeleteMenuOpen] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  /**
   * Hand the invitation to the phone's own share sheet.
   *
   * navigator.share is the right thing where it exists — it opens WhatsApp,
   * Messages, email, whatever that person actually uses, and it works on iPhone
   * Safari too. Where it does not exist (most desktop browsers), the text goes
   * to the clipboard and we say so, rather than a button that appears to do
   * nothing.
   */
  // Only ever visible on a screen where the page really is too wide — which,
  // on a correct layout, is never. It exists so that a phone I do not have can
  // say what is wrong with it, instead of me guessing from a photograph.
  const [tooWide, setTooWide] = useState(null);
  useEffect(() => {
    const check = () => setTooWide(overflowLine());
    const t = setTimeout(check, 700);          // after the page has settled
    window.addEventListener('resize', check);
    return () => { clearTimeout(t); window.removeEventListener('resize', check); };
  }, []);

  const [reloading, setReloading] = useState(false);
  /**
   * Fetch the app again, properly.
   *
   * A plain location.reload() is not enough: the service worker may still be
   * holding the previous version and hand back exactly what is already on
   * screen. So it is told to check for a new one and to step aside if it finds
   * it, and only then does the page reload — otherwise the button would appear
   * to work and change nothing, which is worse than no button.
   */
  async function reloadApp() {
    setReloading(true);
    try {
      const reg = await navigator.serviceWorker?.getRegistration?.();
      if (reg) {
        await reg.update().catch(() => {});
        // Telling the new worker to skip waiting is not the same as it being
        // in charge. It takes over a moment later, and reloading before that
        // moment is answered by the OLD worker with exactly the page that is
        // already on screen — press, wait, nothing changes, press again.
        // Silvia pressed several times, closed the app and reopened it.
        //
        // So: wait for the handover, with a ceiling so a worker that never
        // arrives cannot leave the button spinning for ever.
        if (reg.waiting) {
          await new Promise((resolve) => {
            let settled = false;
            const go = () => { if (!settled) { settled = true; resolve(); } };
            navigator.serviceWorker.addEventListener('controllerchange', go, { once: true });
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
            setTimeout(go, 3000);
          });
        }
      }
      // And ask for the page itself past any cache, so what comes back is
      // what the server has rather than what this device remembers.
      await fetch(window.location.href, { cache: 'reload' }).catch(() => {});
    } catch (_) {}
    window.location.reload();
  }

  const [inviteError, setInviteError] = useState(null);
  const [inviteOpen,  setInviteOpen]  = useState(false);
  async function copyInvite() {
    setInviteError(null);
    const ok = await copyText(inviteText);
    if (ok) {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2500);
      return;
    }
    setInviteError((INVITE_FALLBACK[baseLang] || INVITE_FALLBACK.en) + ' ' + WEB_URL);
  }

  async function handleInvite() {
    setInviteError(null);
    const text = inviteText;
    try {
      if (navigator.share) {
        await navigator.share({ text });
        return;
      }
    } catch (err) {
      // Dismissing the share sheet is not a failure, and telling somebody
      // their own cancellation was an error is worse than silence.
      if (err && err.name === 'AbortError') return;
      // Anything else means the sheet never opened. This is what made the
      // button "stop working": navigator.share EXISTS in the installed app's
      // WebView and then refuses, and every refusal was being treated as a
      // cancellation — so one press did nothing at all, silently. Fall
      // through and copy instead.
    }
    const ok = await copyText(text);
    if (ok) {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2500);
      return;
    }
    // Neither sharing nor copying worked. Say so, with the link in the open,
    // rather than leaving a button that appears to be broken.
    // Shown right under the button, not in the banner at the foot of the
    // form: that is two screens away from where the person just pressed.
    setInviteError((INVITE_FALLBACK[baseLang] || INVITE_FALLBACK.en) + ' ' + WEB_URL);
  }

  // Accessibility options — each independent (device-specific).
  const [a11yContrast, setA11yContrast] = useState(() => { try { return localStorage.getItem('memo_a11y_mode') === '1'; } catch (_) { return false; } });
  const [a11yVibrate,  setA11yVibrate]  = useState(() => { try { return localStorage.getItem('memo_a11y_vibrate') === '1' || localStorage.getItem('memo_a11y_reminders') === '1'; } catch (_) { return false; } });
  const [a11ySpeak,    setA11ySpeak]    = useState(() => { try { return localStorage.getItem('memo_a11y_speak') === '1' || localStorage.getItem('memo_a11y_reminders') === '1'; } catch (_) { return false; } });

  // Font size: 0 = off, 1 = 1.35x, 2 = 1.60x (via `zoom`, the existing mechanism).
  function setBigTo(level) {
    setBigLevel(level);
    try {
      if (level === 0) { localStorage.removeItem('memo_bigtext'); document.documentElement.style.zoom = ''; }
      else { localStorage.setItem('memo_bigtext', String(level)); document.documentElement.style.zoom = level === 2 ? '1.6' : '1.35'; }
    } catch (_) {}
  }

  // High contrast: class on <body> (survives the <html> theme swap; overrides accent).
  function toggleContrast() {
    setA11yContrast(v => {
      const nv = !v;
      try {
        if (nv) { localStorage.setItem('memo_a11y_mode', '1'); document.body.classList.add('a11y-contrast'); }
        else { localStorage.removeItem('memo_a11y_mode'); document.body.classList.remove('a11y-contrast'); }
      } catch (_) {}
      return nv;
    });
  }

  // Vibration + spoken reminders are independent toggles.
  function setVibSpeak(vib, spk) {
    try {
      localStorage.removeItem('memo_a11y_reminders'); // drop the legacy combined flag
      if (vib) localStorage.setItem('memo_a11y_vibrate', '1'); else localStorage.removeItem('memo_a11y_vibrate');
      if (spk) localStorage.setItem('memo_a11y_speak', '1'); else localStorage.removeItem('memo_a11y_speak');
    } catch (_) {}
    setA11yVibrate(vib); setA11ySpeak(spk);
  }
  function toggleVibrate() {
    const next = !a11yVibrate;
    setVibSpeak(next, a11ySpeak);
    // Switching it ON buzzes once, straight away. The switch decides whether
    // REMINDERS vibrate — it is not itself a preview — so without this there
    // is no way to tell "turned on" from "turned on but this phone cannot",
    // and Silvia reasonably read the silence as a broken button.
    if (next) buzz([400]);
  }
  function toggleSpeak()   { setVibSpeak(a11yVibrate, !a11ySpeak); }

  // Instant test: vibrate + speak a sample so it can be verified now.
  //
  // It used to try both and say nothing either way. On a phone that cannot do
  // either — a desktop browser has no vibration, and the installed app's
  // WebView has no speech engine — pressing Prova did nothing at all, with no
  // way to tell a broken button from a device that simply cannot. Silvia:
  // "Prova e vibrazione non sembrano funzionare."
  //
  // Now it reports what actually happened, and when there is no local speech
  // engine it asks the server for the audio, exactly as Today does.
  const [testResult, setTestResult] = useState(null);
  const testingRef = useRef(false);
  const [testing, setTesting] = useState(false);
  async function testA11yReminder() {
    // Pressing again while it is still talking used to lay a second voice over
    // the first.
    if (testingRef.current) return;
    testingRef.current = true;
    setTimeout(() => { testingRef.current = false; }, 1500);
    // Before anything else. A phone takes a moment to wake its voice engine,
    // and a button that shows nothing in that moment has, as far as anybody
    // can tell, not been pressed — so it gets pressed again.
    setTesting(true);
    setTestResult(null);
    let vibrated = false, spoke = false;

    // Bundle 18 declared VIBRATE and it still does not buzz, so the question is
    // now WHICH no this is: no API at all, or an API that answers no. They have
    // different causes and only one of them is ours, and a single sentence for
    // both taught us nothing across a whole build.
    // Which road the buzz took. Shown only when it did NOT happen: it earned
    // its keep — 'web' was the answer that proved the API was fine and the
    // PATTERN was the problem — and a working button should not report on
    // itself. It was shown always for one evening because a marker that
    // appears only on failure cannot be told apart from a screen that has not
    // reloaded, which cost us a round trip.
    // Started, not awaited. The pattern takes 1.2s to play out and the voice
    // has no reason to queue behind it — they are meant to happen together.
    const roadPromise = buzz([500, 200, 500]);
    // 'web' is a promise the installed app cannot keep: navigator.vibrate
    // answers true there and the phone stays still. Only the native road, or a
    // real browser, is evidence that anything happened.


    const phrase = T.settings.voiceSection || 'MeMo';
    try {
      if (window.speechSynthesis) {
        // Two things were wrong with saying this out loud.
        //
        // It came out TWICE, a moment apart. cancel() followed immediately by
        // speak() is a long-standing Android fault: the queue is still being
        // torn down when the new utterance arrives, and it can be started
        // twice. Cancelling and then speaking on the next tick is the cure.
        //
        // And it sounded wrong because the utterance had no language on it, so
        // the phone read Italian words with whatever engine it had to hand.
        // Every other read-out in MeMo sets this; this one did not.
        window.speechSynthesis.cancel();
        // The list of voices arrives asynchronously and is empty on the first
        // call after a load. Without this the first press used the engine's
        // default and the second used the chosen voice — the same button
        // reading the same words two different ways.
        await voicesReady();
        await new Promise(r => setTimeout(r, 120));
        const u = new SpeechSynthesisUtterance(phrase);
        u.lang = langToSpeechLang(form.language);
        applyTTSPrefs(u);
        window.speechSynthesis.speak(u);
        spoke = true;
      } else {
        const res = await apiClient.post('/speak',
          { text: phrase, language: user?.language || 'it' },
          { responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        const audio = new Audio(url);
        audio.onended = () => URL.revokeObjectURL(url);
        await audio.play();
        spoke = true;
      }
    } catch (_) {}

    const road = await roadPromise;
    // 'web' is a promise the installed app cannot keep: navigator.vibrate
    // answers true there and the phone stays still. Only the native road, or a
    // real browser, is evidence that anything happened.
    const webIsHonest = !isNativeApp();
    vibrated = road === 'native' || (road === 'web' && webIsHonest);
    const askedButUnsure = road === 'web' && !webIsHonest;
    const vibeWhy = road;
    const R = TEST_RESULT[baseLang] || TEST_RESULT.en;
    const said = askedButUnsure ? R.soon
      : spoke && vibrated ? R.both
      : spoke ? R.spokeOnly
      : vibrated ? R.vibeOnly
      : R.neither;
    // The marker is for whoever is reporting the fault, not for everyday use:
    // it only appears when the buzz did not happen, and it says why in four
    // words so a tester can pass it on without knowing what it means.
    setTestResult(vibrated ? said : said + '  (' + vibeWhy + ')');
    setTesting(false);
  }

  useEffect(() => {
    apiClient.get('/usage').then(r => setAiUsage(r.data)).catch(() => {});
  }, []);
  const [form, setForm] = useState(() => ({
    language: remapLang(user?.language),
    summary_time: user?.summary_time || '07:00',
    timezone: user?.timezone || 'Europe/Rome',
    telegram_chat_id: user?.telegram_chat_id || '',
    theme: user?.theme || 'light',
    summary_email: user?.summary_email || '',
  }));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saveErrorMsg, setSaveErrorMsg] = useState(null);     // actual error string from server/network
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [telegramResult, setTelegramResult] = useState(null); // { ok, apiError? }  — message resolved at render
  const [testingPush, setTestingPush]       = useState(false);
  const [pushTestResult, setPushTestResult] = useState(null); // { ok, error? }
  const [botUsername, setBotUsername] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null); // { ok, error? }
  // Channel toggles — derived from saved values, controlled locally
  const [notifyTelegram, setNotifyTelegram] = useState(() => Boolean(user?.telegram_chat_id));
  const [notifyEmail, setNotifyEmail] = useState(() => Boolean(user?.summary_email));

  // Alarm settings — localStorage only (device-specific preferences)
  const [alarmVolume, setAlarmVolume]     = useState(() => parseFloat(localStorage.getItem(ALARM_KEYS.volume)    ?? '0.7'));
  const [alarmSnooze, setAlarmSnooze]     = useState(() => parseInt(localStorage.getItem(ALARM_KEYS.snoozeMin)   ?? '5', 10));
  const [alarmMaxSnooze, setAlarmMaxSnooze] = useState(() => parseInt(localStorage.getItem(ALARM_KEYS.maxSnooze) ?? '3', 10));
  const [alarmSound, setAlarmSound]       = useState(() => localStorage.getItem(ALARM_KEYS.sound) || 'campanella');
  // Default notification (reminder) settings — ON by default
  const [notifEnabled, setNotifEnabled]     = useState(() => getNotifSettings().enabled);
  const [notifDefaultMin, setNotifDefaultMin] = useState(() => getNotifSettings().defaultMin);
  const [notifDefaultType, setNotifDefaultType] = useState(() => getNotifSettings().defaultType);

  // Weather permission — managed here, reflected in TodayView via localStorage
  const [weatherPerm, setWeatherPerm]   = useState(() => localStorage.getItem(WEATHER_KEY) ?? 'unknown');
  const [weatherActivating, setWeatherActivating] = useState(false);
  const [weatherError, setWeatherError] = useState(null); // 'denied' | 'unavailable' | null

  function activateWeather() {
    if (!navigator?.geolocation) {
      setWeatherError('unavailable');
      return;
    }
    setWeatherActivating(true);
    setWeatherError(null);
    navigator.geolocation.getCurrentPosition(
      () => {
        // Success — browser granted location access
        localStorage.setItem(WEATHER_KEY, 'granted');
        setWeatherPerm('granted');
        setWeatherActivating(false);
      },
      (err) => {
        setWeatherActivating(false);
        if (err.code === 1) {
          // User tapped "Block" in the browser dialog
          localStorage.setItem(WEATHER_KEY, 'denied');
          setWeatherPerm('denied');
          setWeatherError('denied');
        } else {
          // GPS unavailable / timeout — save 'granted' anyway so it retries in TodayView
          localStorage.setItem(WEATHER_KEY, 'granted');
          setWeatherPerm('granted');
        }
      },
      { timeout: 10000, maximumAge: 0 },
    );
  }

  function disableWeather() {
    localStorage.removeItem(WEATHER_KEY);
    setWeatherPerm('unknown');
    setWeatherError(null);
  }

  // Perla del giorno — saved on the account (the morning summary needs it too),
  // ON unless explicitly turned off. Optimistic: the button flips at once.
  const [pearlEnabled, setPearlEnabled] = useState(() => user?.pearl_enabled !== false);
  useEffect(() => { setPearlEnabled(user?.pearl_enabled !== false); }, [user?.pearl_enabled]);

  // Per-event reminders repeated in the Telegram chat (the morning summary is a
  // separate thing and always arrives). Same optimistic pattern as the pearl.
  const [telegramAlerts, setTelegramAlerts] = useState(() => user?.telegram_alerts !== false);
  useEffect(() => { setTelegramAlerts(user?.telegram_alerts !== false); }, [user?.telegram_alerts]);
  function toggleTelegramAlerts() {
    const next = !telegramAlerts;
    setTelegramAlerts(next);
    setUser(u => (u ? { ...u, telegram_alerts: next } : u));
    apiClient.put('/settings', { telegram_alerts: next }).catch((err) => {
      setTelegramAlerts(!next);                                 // revert on failure
      setUser(u => (u ? { ...u, telegram_alerts: !next } : u));
      // Say it, don't just undo it. A switch that flicks back on its own
      // reads as a mis-tap, so the person tries again — and again. The banner
      // this page already uses for a failed save says what actually happened.
      setSaveErrorMsg(err?.response?.data?.error || err?.message || null);
      setSaveError(true);
    });
  }

  // Morning summary only on days that have events — users.summary_skip_empty,
  // OFF unless explicitly turned on (default = the summary always arrives).
  const [skipEmptySummary, setSkipEmptySummary] = useState(() => user?.summary_skip_empty === true);
  useEffect(() => { setSkipEmptySummary(user?.summary_skip_empty === true); }, [user?.summary_skip_empty]);
  function toggleSkipEmptySummary() {
    const next = !skipEmptySummary;
    setSkipEmptySummary(next);
    setUser(u => (u ? { ...u, summary_skip_empty: next } : u));
    apiClient.put('/settings', { summary_skip_empty: next }).catch((err) => {
      setSkipEmptySummary(!next);                               // revert on failure
      setUser(u => (u ? { ...u, summary_skip_empty: !next } : u));
      setSaveErrorMsg(err?.response?.data?.error || err?.message || null);
      setSaveError(true);
    });
  }

  function togglePearl() {
    const next = !pearlEnabled;
    setPearlEnabled(next);
    setUser(u => (u ? { ...u, pearl_enabled: next } : u));
    apiClient.put('/settings', { pearl_enabled: next }).catch((err) => {
      setPearlEnabled(!next);                                   // revert on failure
      setUser(u => (u ? { ...u, pearl_enabled: !next } : u));
      setSaveErrorMsg(err?.response?.data?.error || err?.message || null);
      setSaveError(true);
    });
  }

  // PWA install prompt
  const [installPrompt, setInstallPrompt] = useState(null);
  const [appInstalled, setAppInstalled] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const installed = () => { setAppInstalled(true); setInstallPrompt(null); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  // Sync form whenever user data changes (login, reload, or external update)
  useEffect(() => {
    if (user) {
      setForm({
        language: remapLang(user.language),
        summary_time: user.summary_time || '07:00',
        timezone: user.timezone || 'Europe/Rome',
        telegram_chat_id: user.telegram_chat_id || '',
        theme: user.theme || 'light',
        summary_email: user.summary_email || '',
      });
      setNotifyTelegram(Boolean(user.telegram_chat_id));
      setNotifyEmail(Boolean(user.summary_email));
    }
  }, [user?.id, user?.language, user?.summary_email, user?.timezone, user?.theme, user?.telegram_chat_id]);

  // Use form.language for live translation preview as user changes the dropdown
  const T = useT(form.language);
  const baseLang = (form.language || 'it').split('-')[0];
  // The message itself, so the preview and every button send exactly the same
  // words — there is no second version of it anywhere. It has to be built
  // AFTER baseLang: a const cannot be read above its own declaration, and
  // doing so took the whole screen down behind the error boundary.
  const inviteText = (INVITE_TEXT[baseLang] || INVITE_TEXT.en)(PLAY_URL, WEB_URL);
  const A        = a11y(form.language);   // names for the symbol-only buttons

  // The speed measurement, reachable but not on display. Five taps on the
  // version line at the bottom reveals the link.
  //
  // It cannot simply be removed and reached by typing memocal.app/velocita: the
  // installed app is a WebView, and a WebView's localStorage is its own. The
  // numbers live there, so the same address opened in Chrome shows Chrome's
  // measurements — which is to say, almost none. The only door to the app's own
  // figures has to be inside the app.
  //
  // Declared here, far above the JSX that reads it: state read from above where
  // it is defined is a temporal dead zone, and React answers that by unmounting
  // the whole screen. That mistake cost a blank page on the ＋ button once.
  const [diagTaps, setDiagTaps] = useState(0);
  const diagTimer = useRef(null);
  const showDiag = diagTaps >= 5;
  function tapVersion() {
    // The run resets after a second and a half of stillness, so five ordinary
    // taps count and five accidental brushes spread over a minute do not.
    clearTimeout(diagTimer.current);
    diagTimer.current = setTimeout(() => setDiagTaps(0), 1500);
    setDiagTaps(n => n + 1);
  }
  useEffect(() => () => clearTimeout(diagTimer.current), []);

  useEffect(() => {
    apiClient.get('/settings/bot-info')
      .then((res) => {
        if (res.data?.username) setBotUsername(res.data.username);
      })
      .catch(() => {});
  }, []);

  // Every setting saves itself. There is no button any more, and this is not
  // only tidier — it closes a fault we had already met and not recognised.
  //
  // Loraine, in the United States: "I checked the settings and it still says
  // 6:00am - what I set it as", and then no summary arrived. Six of these
  // fields only ever reached the server when "Salva impostazioni" was pressed.
  // Change the time, leave the page, and the screen went on showing 6:00 from
  // the form's own memory while the server still had 7:00 — the app showing a
  // setting that was not in force anywhere. Nothing warned; nothing could,
  // because from the page's point of view nothing was wrong.
  //
  // Text fields wait 800ms after the last keystroke; anything picked (a time,
  // a city, a theme) goes at once, because a tap is already a decision.
  const saveTimer = React.useRef(null);
  function saveNow(patch) {
    clearTimeout(saveTimer.current);
    setSaveError(false);
    setSaving(true);
    apiClient.put('/settings', patch)
      .then(res => {
        const stored = res.data?.settings || {};
        setUser(u => ({ ...u, ...patch, ...stored }));
        setSaved(true);
        setTimeout(() => setSaved(false), 1600);
      })
      .catch(err => {
        // Said, and said where it happened. A setting that did not reach the
        // server must never look like one that did.
        setSaveErrorMsg(err.response?.data?.error || err.message || null);
        setSaveError(true);
      })
      .finally(() => setSaving(false));
  }
  function saveSoon(patch) {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveNow(patch), 800);
  }
  React.useEffect(() => () => clearTimeout(saveTimer.current), []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // A typed field is still being typed; a chosen one is finished.
    if (name === 'telegram_chat_id' || name === 'summary_email') saveSoon({ [name]: value });
    else saveNow({ [name]: value });
  }

  function handleThemeChange(value) {
    setForm((f) => ({ ...f, theme: value }));
    document.documentElement.className = value === 'dark' ? 'theme-dark' : '';
    saveNow({ theme: value });
  }

  // Large-text accessibility (device-specific, localStorage). Two levels: 1 =
  // Large (zoom 1.35), 2 = Extra large (1.6). Tapping the active level turns it
  // off. Uses CSS zoom so the fluid layout reflows — text grows without
  // horizontal panning, preserving one-handed use. Applied on load in index.js.
  function chooseBig(level) {
    const next = bigLevel === level ? 0 : level;
    setBigLevel(next);
    try {
      if (next === 0) { localStorage.removeItem('memo_bigtext'); document.documentElement.style.zoom = ''; }
      else { localStorage.setItem('memo_bigtext', String(next)); document.documentElement.style.zoom = next === 2 ? '1.6' : '1.35'; }
    } catch (_) {}
  }

  // App accent colour (device-specific). '' = default sage.
  function chooseAccent(hex) {
    setAccent(hex);
    try {
      if (hex) localStorage.setItem('memo_accent', hex);
      else localStorage.removeItem('memo_accent');
    } catch (_) {}
    applyAccent(hex);
  }

  // Language applies IMMEDIATELY on selection — no separate Save tap. It saves
  // ONLY the language (decoupled from the rest of the form, so an unrelated
  // invalid field can never block it), updates the user, then reloads so the
  // whole app (dates, RTL, cached labels) reboots in the chosen language. This
  // is what stops the UI snapping back to the old language when you change tab.
  async function handleLanguageChange(e) {
    const value = e.target.value;
    setForm((f) => ({ ...f, language: value })); // instant preview
    try {
      await apiClient.put('/settings', { language: value });
      setUser((u) => (u ? { ...u, language: value } : u));
      window.location.assign('/');
    } catch (err) {
      // Persist failed: snap back to the real saved value and surface the error
      setForm((f) => ({ ...f, language: remapLang(user?.language) }));
      setSaveErrorMsg(err.response?.data?.error || null);
      setSaveError(true);
    }
  }

  async function handleTestTelegram() {
    setTestingTelegram(true);
    setTelegramResult(null);
    try {
      await apiClient.post('/settings/test-telegram');
      setTelegramResult({ ok: true });
    } catch (err) {
      // Store the API-provided error string (already in the right language from backend),
      // or null to fall back to the translated T key at render time
      setTelegramResult({ ok: false, apiError: err.response?.data?.error || null });
    } finally {
      setTestingTelegram(false);
    }
  }

  async function handleTestEmail() {
    setTestingEmail(true);
    setEmailTestResult(null);
    try {
      // Save email to DB first so it always persists
      if (form.summary_email) {
        await apiClient.put('/settings', { summary_email: form.summary_email });
      }
      await apiClient.post('/settings/test-email', { email: form.summary_email });
      setEmailTestResult({ ok: true });
      // Update user context so email stays visible in field
      setUser((u) => ({ ...u, summary_email: form.summary_email }));
    } catch (err) {
      setEmailTestResult({ ok: false, error: err.response?.data?.error || null });
    } finally {
      setTestingEmail(false);
    }
  }

  // handleSave is gone with its button. Language already reloads the page by
  // itself (handleLanguageChange) and every other field saves as it changes,
  // so a submit would only repeat work that has already been done.
  function handleSubmit(e) { e.preventDefault(); }

  const themeButtonBase = {
    flex: 1,
    padding: '10px 8px',
    borderRadius: 'var(--radius-sm)',
    border: '2px solid var(--color-border)',
    background: 'var(--color-card)',
    color: 'var(--color-text)',
    fontSize: '0.92rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  };

  const themeButtonSelected = {
    border: '2px solid var(--color-primary)',
    background: 'rgba(122,158,126,0.1)',
    color: 'var(--color-primary-dark)',
  };

  // Thin full-width divider — sections handle vertical spacing via their own padding
  const Divider = () => (
    <div style={{ height: 1, background: 'var(--color-border)' }} />
  );

  // The headings INSIDE the Ora panel: the same size and weight as EUROPA,
  // AMERICA and the rest, which are the very next headings down the page.
  const PanelLabel = ({ children }) => (
    <div style={{
      fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em',
      textTransform: 'uppercase', color: 'var(--color-text-muted)',
      margin: '0 0 6px',
    }}>
      {children}
    </div>
  );

  // Compact section label — matches .form-label exactly (0.8rem / 600 / 0.04em)
  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-muted)', marginBottom: 10 }}>
      {children}
    </div>
  );

  // Compact header: section label on the left, its action control on the
  // right of the SAME row — recovers vertical space throughout the page.
  const SectionRow = ({ label, children, danger }) => (
    // flexWrap: a long label + a wide action button (e.g. "Contatta il supporto
    // su Telegram") that don't fit side by side now wrap to a second line
    // instead of pushing the page wider than the phone screen.
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
      <div style={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: danger ? '#c0392b' : 'var(--color-text-muted)', minWidth: 0 }}>
        {label}
      </div>
      {children}
    </div>
  );

  // Delete-account strings (kept local; falls back to English for languages
  // not listed here).
  const DELETE_T = {
    it: { title: 'Attenzione', desc: 'Elimina per sempre il tuo account e tutti i tuoi dati (eventi, note, attività, etichette, calendari). Non si può annullare.', btn: 'Elimina account', deleting: 'Eliminazione…', confirm: 'Vuoi davvero eliminare il tuo account?\n\nTutti i tuoi dati verranno cancellati per sempre. Questa azione è irreversibile.', error: "Impossibile eliminare l'account. Riprova." },
    en: { title: 'Warning', desc: 'Permanently delete your account and all your data (events, notes, tasks, labels, calendars). This cannot be undone.', btn: 'Delete account', deleting: 'Deleting…', confirm: 'Do you really want to delete your account?\n\nAll your data will be permanently deleted. This action cannot be undone.', error: 'Could not delete the account. Please try again.' },
    es: { title: 'Atención', desc: 'Elimina para siempre tu cuenta y todos tus datos (eventos, notas, tareas, etiquetas, calendarios). No se puede deshacer.', btn: 'Eliminar cuenta', deleting: 'Eliminando…', confirm: '¿Seguro que quieres eliminar tu cuenta?\n\nTodos tus datos se eliminarán para siempre. Esta acción es irreversible.', error: 'No se pudo eliminar la cuenta. Inténtalo de nuevo.' },
    de: { title: 'Achtung', desc: 'Lösche dein Konto und alle Daten (Termine, Notizen, Aufgaben, Labels, Kalender) endgültig. Das kann nicht rückgängig gemacht werden.', btn: 'Konto löschen', deleting: 'Wird gelöscht…', confirm: 'Möchtest du dein Konto wirklich löschen?\n\nAlle deine Daten werden endgültig gelöscht. Dies kann nicht rückgängig gemacht werden.', error: 'Konto konnte nicht gelöscht werden. Bitte erneut versuchen.' },
    fr: { title: 'Attention', desc: 'Supprimez définitivement votre compte et toutes vos données (événements, notes, tâches, étiquettes, agendas). Irréversible.', btn: 'Supprimer le compte', deleting: 'Suppression…', confirm: 'Voulez-vous vraiment supprimer votre compte ?\n\nToutes vos données seront supprimées définitivement. Cette action est irréversible.', error: 'Impossible de supprimer le compte. Réessayez.' },
    pt: { title: 'Atenção', desc: 'Elimina para sempre a tua conta e todos os teus dados (eventos, notas, tarefas, etiquetas, calendários). Não pode ser anulado.', btn: 'Eliminar conta', deleting: 'A eliminar…', confirm: 'Queres mesmo eliminar a tua conta?\n\nTodos os teus dados serão eliminados para sempre. Esta ação é irreversível.', error: 'Não foi possível eliminar a conta. Tenta novamente.' },
    nl: { title: 'Let op', desc: 'Verwijder je account en alle gegevens (afspraken, notities, taken, labels, agenda’s) definitief. Dit kan niet ongedaan worden gemaakt.', btn: 'Account verwijderen', deleting: 'Verwijderen…', confirm: 'Wil je je account echt verwijderen?\n\nAl je gegevens worden definitief verwijderd. Dit kan niet ongedaan worden gemaakt.', error: 'Kon het account niet verwijderen. Probeer opnieuw.' },
    ru: { title: 'Внимание', desc: 'Безвозвратно удалить аккаунт и все данные (события, заметки, задачи, метки, календари). Отменить нельзя.', btn: 'Удалить аккаунт', deleting: 'Удаление…', confirm: 'Вы действительно хотите удалить аккаунт?\n\nВсе ваши данные будут удалены навсегда. Это действие необратимо.', error: 'Не удалось удалить аккаунт. Попробуйте снова.' },
    pl: { title: 'Uwaga', desc: 'Trwale usuń swoje konto i wszystkie dane (wydarzenia, notatki, zadania, etykiety, kalendarze). Tego nie można cofnąć.', btn: 'Usuń konto', deleting: 'Usuwanie…', confirm: 'Czy na pewno chcesz usunąć swoje konto?\n\nWszystkie dane zostaną trwale usunięte. Tej operacji nie można cofnąć.', error: 'Nie udało się usunąć konta. Spróbuj ponownie.' },
    ro: { title: 'Atenție', desc: 'Șterge definitiv contul și toate datele (evenimente, notițe, sarcini, etichete, calendare). Nu se poate anula.', btn: 'Șterge contul', deleting: 'Se șterge…', confirm: 'Chiar vrei să îți ștergi contul?\n\nToate datele tale vor fi șterse definitiv. Această acțiune este ireversibilă.', error: 'Contul nu a putut fi șters. Încearcă din nou.' },
    zh: { title: '注意', desc: '永久删除你的账户和所有数据（日程、笔记、任务、标签、日历）。此操作无法撤销。', btn: '删除账户', deleting: '删除中…', confirm: '确定要删除账户吗？\n\n所有数据将被永久删除。此操作不可撤销。', error: '无法删除账户，请重试。' },
    ja: { title: '注意', desc: 'アカウントとすべてのデータ（予定・メモ・タスク・ラベル・カレンダー）を完全に削除します。元に戻せません。', btn: 'アカウントを削除', deleting: '削除中…', confirm: '本当にアカウントを削除しますか？\n\nすべてのデータが完全に削除されます。この操作は取り消せません。', error: 'アカウントを削除できませんでした。もう一度お試しください。' },
    hi: { title: 'ध्यान दें', desc: 'अपना खाता और सारा डेटा (इवेंट, नोट्स, कार्य, लेबल, कैलेंडर) हमेशा के लिए हटाएँ। इसे पूर्ववत नहीं किया जा सकता।', btn: 'खाता हटाएँ', deleting: 'हटाया जा रहा है…', confirm: 'क्या आप वाकई अपना खाता हटाना चाहते हैं?\n\nआपका सारा डेटा हमेशा के लिए हट जाएगा। यह क्रिया अपरिवर्तनीय है।', error: 'खाता नहीं हटाया जा सका। फिर से प्रयास करें।' },
    ko: { title: '주의', desc: '계정과 모든 데이터(일정, 메모, 할 일, 라벨, 캘린더)를 영구 삭제합니다. 되돌릴 수 없습니다.', btn: '계정 삭제', deleting: '삭제 중…', confirm: '정말 계정을 삭제하시겠습니까?\n\n모든 데이터가 영구 삭제됩니다. 이 작업은 되돌릴 수 없습니다.', error: '계정을 삭제할 수 없습니다. 다시 시도해 주세요.' },
    ar: { title: 'تنبيه', desc: 'احذف حسابك وجميع بياناتك نهائيًا (الأحداث، الملاحظات، المهام، التصنيفات، التقويمات). لا يمكن التراجع.', btn: 'حذف الحساب', deleting: 'جارٍ الحذف…', confirm: 'هل تريد حقًا حذف حسابك؟\n\nستُحذف جميع بياناتك نهائيًا. هذا الإجراء لا رجعة فيه.', error: 'تعذّر حذف الحساب. حاول مجددًا.' },
  };
  const dl = DELETE_T[(form.language || 'it').split('-')[0]] || DELETE_T.en;

  // Free-plan AI usage counter labels (falls back to English).
  const USAGE_T = {
    it: { title: 'Utilizzo voce e foto questo mese', sub: 'azioni voce + foto', nudge: 'Passa a Personal per voce e foto illimitate.' },
    en: { title: 'Voice & photo usage this month', sub: 'voice + photo actions', nudge: 'Upgrade to Personal for unlimited voice & photo.' },
    es: { title: 'Uso de voz y foto este mes', sub: 'acciones de voz + foto', nudge: 'Pasa a Personal para voz y foto ilimitadas.' },
    de: { title: 'Sprach- & Foto-Nutzung diesen Monat', sub: 'Sprach- + Foto-Aktionen', nudge: 'Wechsle zu Personal für unbegrenzte Sprache & Fotos.' },
    fr: { title: 'Utilisation voix et photo ce mois-ci', sub: 'actions voix + photo', nudge: 'Passez à Personal pour voix et photo illimitées.' },
    pt: { title: 'Uso de voz e foto este mês', sub: 'ações de voz + foto', nudge: 'Passa para Personal para voz e foto ilimitadas.' },
    nl: { title: 'Spraak- & fotogebruik deze maand', sub: 'spraak- + foto-acties', nudge: 'Ga naar Personal voor onbeperkt spraak & foto.' },
    ru: { title: 'Использование голоса и фото в этом месяце', sub: 'действия: голос + фото', nudge: 'Перейдите на Personal для безлимита голоса и фото.' },
    pl: { title: 'Zużycie głosu i zdjęć w tym miesiącu', sub: 'akcje głos + zdjęcia', nudge: 'Przejdź na Personal — nieograniczony głos i zdjęcia.' },
    ro: { title: 'Utilizare voce și foto luna aceasta', sub: 'acțiuni voce + foto', nudge: 'Treci la Personal pentru voce și foto nelimitate.' },
    zh: { title: '本月语音和照片使用量', sub: '语音 + 照片操作', nudge: '升级到 Personal，语音和照片无限。' },
    ja: { title: '今月の音声・写真の利用状況', sub: '音声・写真の操作', nudge: 'Personal にアップグレードで音声・写真が無制限。' },
    hi: { title: 'इस महीने आवाज़ और फ़ोटो उपयोग', sub: 'आवाज़ + फ़ोटो क्रियाएँ', nudge: 'असीमित आवाज़ और फ़ोटो के लिए Personal पर जाएं।' },
    ko: { title: '이번 달 음성·사진 사용량', sub: '음성·사진 작업', nudge: 'Personal로 업그레이드하면 음성·사진 무제한.' },
    ar: { title: 'استخدام الصوت والصور هذا الشهر', sub: 'عمليات الصوت + الصور', nudge: 'ترقَّ إلى Personal لصوت وصور بلا حدود.' },
  };
  const ul = USAGE_T[(form.language || 'it').split('-')[0]] || USAGE_T.en;
  const usageUnlimited = aiUsage && aiUsage.limit === null; // paid/comped plan: no cap
  const usageLimit = aiUsage?.limit || 30;
  const usageCount = aiUsage?.count || 0;
  const usagePct = Math.min(100, Math.round((usageCount / usageLimit) * 100));

  async function handleDeleteAccount() {
    if (!window.confirm(dl.confirm)) return;
    setDeletingAccount(true);
    setDeleteAccountErr('');
    try {
      await apiClient.delete('/auth/account');
      setUser(null);
      navigate('/');
    } catch (e) {
      setDeletingAccount(false);
      setDeleteAccountErr(dl.error);
    }
  }

  return (
    <div className="settings-page" style={{ paddingBottom: 80 }}>

      {/* ── User row ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '16px 0 14px' }}>
        <div className="user-info" style={{ margin: 0 }}>
          {user?.picture && (
            <img src={user.picture} alt="avatar" className="avatar" referrerPolicy="no-referrer" style={{ width: 34, height: 34 }} />
          )}
          <div>
            <div className="user-name" style={{ fontSize: '0.9rem' }}>{user?.name}</div>
            <div className="user-email" style={{ fontSize: '0.75rem' }}>{user?.email}</div>
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={logout} style={{ flexShrink: 0, fontSize: '0.92rem' }}>
          {T.settings.signOut}
        </button>
      </div>

      {/* AI usage counter moved into the "Utilizzo" dropdown in the third row. */}

      <form onSubmit={handleSubmit}>

        <Divider />

        {/* ── 1. Lingua · Fuso orario · Voce Lettura ────────────────────── */}
        <div style={{ padding: '18px 0' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            {/* Lingua */}
            <button type="button" onClick={() => { setLangMenuOpen(o => !o); setTzMenuOpen(false); setThemeMenuOpen(false); }} aria-expanded={langMenuOpen}
              style={{ ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'var(--color-card)', color: 'var(--color-text)', border: langMenuOpen ? '2px solid var(--color-primary)' : '2px solid var(--color-border)' }}>
              <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>{(LANGUAGES.find(l => l.value === form.language)?.label || '🌐').trim().split(/\s+/)[0]}</span>
              <span className="tile-label" style={{ textAlign: 'center' }}>{T.settings.language} {langMenuOpen ? '▲' : '▾'}</span>
            </button>
            {/* Fuso orario — label fisso, la città si sceglie dentro */}
            <button type="button" onClick={() => { setTzMenuOpen(o => !o); setLangMenuOpen(false); setThemeMenuOpen(false); }} aria-expanded={tzMenuOpen}
              style={{ ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'var(--color-card)', color: 'var(--color-text)', border: tzMenuOpen ? '2px solid var(--color-primary)' : '2px solid var(--color-border)' }}>
              {/* "Fuso orario" was right when the panel held only a city. It now
                  holds the clock and the first day of the week too, and Silvia's
                  point is the one that matters: the time zone is something you
                  set once when you sign up, so somebody looking for the AM/PM
                  switch would never think to open a tile about time zones. */}
              <span className="tile-label" style={{ textAlign: 'center' }}>{({ it: 'Orario', en: 'Time', es: 'Hora', de: 'Zeit', fr: 'Heure', nl: 'Tijd', pt: 'Hora', 'pt-BR': 'Hora', ru: 'Время', pl: 'Czas', ro: 'Ora', zh: '时间', ja: '時刻', hi: 'समय', ko: '시간', ar: 'الوقت' }[baseLang] || 'Time')} {tzMenuOpen ? '▲' : '▾'}</span>
            </button>
            {/* Tema App — schermo + colori app + meteo */}
            <button type="button" onClick={() => { setThemeMenuOpen(o => !o); setLangMenuOpen(false); setTzMenuOpen(false); }} aria-expanded={themeMenuOpen}
              style={{ ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'var(--color-card)', color: 'var(--color-text)', border: themeMenuOpen ? '2px solid var(--color-primary)' : '2px solid var(--color-border)' }}>
              <span className="tile-label" style={{ textAlign: 'center' }}>{({ it: 'Tema App', en: 'App theme', es: 'Tema', de: 'App-Design', fr: 'Thème', nl: 'Thema', pt: 'Tema', ru: 'Тема', pl: 'Motyw', ro: 'Temă', zh: '主题', ja: 'テーマ', hi: 'थीम', ko: '테마', ar: 'المظهر' }[baseLang] || 'App theme')} {themeMenuOpen ? '▲' : '▾'}</span>
            </button>
          </div>

          {/* Lingua — scegli da un elenco */}
          {langMenuOpen && (
            <div style={{ marginTop: 12 }}>
              {/* Buttons, not a dropdown — and the reason is the flags.
                  A native <select> is drawn by the phone, not by us, and
                  Android's own list font has no flag emoji: the same 🇮🇹 that
                  shows on the landing page came out as two grey letters here,
                  or as an empty box. Nothing was wrong with the emoji; it was
                  the widget. Ordinary buttons use the page's font, so the flags
                  arrive — and a language list is a row of choices anyway,
                  not a form field. */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 8, maxHeight: 320, overflowY: 'auto',
              }}>
                {LANGUAGES.map((l) => {
                  const active = form.language === l.value;
                  // The label is "🇮🇹      Italiano": flag first, then the name
                  // written in that language.
                  const parts = l.label.trim().split(/\s+/);
                  const flag = parts[0];
                  const name = parts.slice(1).join(' ');
                  return (
                    <button
                      key={l.value}
                      type="button"
                      onClick={() => handleLanguageChange({ target: { value: l.value } })}
                      aria-pressed={active}
                      className="tap-grow"
                      style={{
                        '--tap-x': '4px',
                        display: 'flex', alignItems: 'center', gap: 8,
                        minHeight: 46, padding: '8px 10px',
                        fontFamily: 'inherit', fontSize: '0.9rem',
                        fontWeight: active ? 800 : 500,
                        textAlign: 'start', cursor: 'pointer',
                        borderRadius: 10,
                        background: active ? 'var(--color-primary-light)' : 'var(--color-card)',
                        color: 'var(--color-text)',
                        border: active ? '2px solid var(--color-primary)' : '1.5px solid var(--color-border)',
                      }}
                    >
                      <span style={{ fontSize: '1.25rem', lineHeight: 1, flexShrink: 0 }} aria-hidden="true">{flag}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ora — scegli la città (orologi tolti dall'elenco, uno solo resta nell'etichetta) */}
          {tzMenuOpen && (
            <div style={{ marginTop: 12 }}>
              {/* Twelve hours or twenty-four, under "Ora" because it is the same
                  subject as the city: how a time is written. MeMo asks the
                  phone by itself and gets it right nearly always — that is what
                  fixed Loraine's "military time" — but guessing well is not the
                  same as being sure. Somebody with an English phone who wants
                  24 hours had no way to say so. */}
              {(() => {
                const CW = CLOCK_WORDS[form.language] || CLOCK_WORDS[String(form.language).split('-')[0]] || CLOCK_WORDS.en;
                const opts = [['auto', CW.auto], ['24', CW.h24], ['12', CW.h12]];
                return (
                  <div style={{ marginBottom: 16 }}>
                    {/* The same SectionLabel every other heading on this page
                        uses — not a copy of its numbers, so OROLOGIO cannot
                        drift away from the rest the next time one is touched. */}
                    <PanelLabel>{CW.t}</PanelLabel>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {opts.map(([v, label]) => {
                        const on = clockPref === v;
                        return (
                          <button
                            key={v}
                            type="button"
                            aria-pressed={on}
                            onClick={() => { setClockPreference(v); setClockPref(v); }}
                            style={{
                              flex: 1, minWidth: 0, minHeight: 42, padding: '6px 4px',
                              fontFamily: 'inherit', fontSize: '0.76rem', fontWeight: on ? 800 : 500,
                              borderRadius: 10, cursor: 'pointer', lineHeight: 1.2,
                              background: on ? 'var(--color-primary-light)' : 'var(--color-card)',
                              color: 'var(--color-text)',
                              border: on ? '2px solid var(--color-primary)' : '1.5px solid var(--color-border)',
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Monday or Sunday. MeMo has always begun the week on Monday,
                  which is right for Italy and wrong for the United States — and
                  MeMo now has testers in both. The two names are taken from the
                  app's own date locale rather than a table, so they are already
                  correct in all sixteen languages and there is nothing to keep
                  up to date. */}
              {(() => {
                // CW is worked out again here rather than borrowed from the
                // clock's block above: that block is a separate function, and
                // reaching into it from this one is what took the whole Orario
                // panel down.
                const CW = CLOCK_WORDS[form.language] || CLOCK_WORDS[String(form.language).split('-')[0]] || CLOCK_WORDS.en;
                const loc = DATE_LOCALES[form.language] || DATE_LOCALES[String(form.language).split('-')[0]] || DATE_LOCALES.it;
                // 5 Jan 2026 is a Monday, so +0 is Monday and −1 is the Sunday
                // before it: two real dates, formatted by the locale itself.
                const monday = new Date(2026, 0, 5);
                const sunday = new Date(2026, 0, 4);
                const nameOf = d => {
                  const n = dfFormat(d, 'EEEE', { locale: loc });
                  return n.charAt(0).toUpperCase() + n.slice(1);
                };
                return (
                  <div style={{ marginBottom: 16 }}>
                    <PanelLabel>{CLOCK_WORDS[form.language]?.week
                      || CLOCK_WORDS[String(form.language).split('-')[0]]?.week
                      || CLOCK_WORDS.en.week}</PanelLabel>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[['auto', CW.auto], ['sun', nameOf(sunday)], ['mon', nameOf(monday)]].map(([v, label]) => {
                        const on = weekPref === v;
                        return (
                          <button
                            key={v}
                            type="button"
                            aria-pressed={on}
                            onClick={() => {
                              setWeekPreference(v);
                              setWeekPref(v);
                              // Four screens read this once and keep it. A
                              // reload is the honest way to make all of them
                              // agree at the same moment, instead of a week
                              // view that has changed and a month that has not.
                              setTimeout(() => window.location.reload(), 250);
                            }}
                            style={{
                              flex: 1, minWidth: 0, minHeight: 42, padding: '6px 4px',
                              fontFamily: 'inherit', fontSize: '0.76rem', fontWeight: on ? 800 : 500,
                              borderRadius: 10, cursor: 'pointer', lineHeight: 1.2,
                              background: on ? 'var(--color-primary-light)' : 'var(--color-card)',
                              color: 'var(--color-text)',
                              border: on ? '2px solid var(--color-primary)' : '1.5px solid var(--color-border)',
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <TimezonePicker
                value={form.timezone}
                language={form.language}
                ariaLabel={T.settings.timezoneTab || 'Fuso orario'}
                onChange={(tz) => handleChange({ target: { name: 'timezone', value: tz } })}
              />
            </div>
          )}

          {/* Tema App — schermo (chiaro/scuro) + colore app + meteo */}
          {themeMenuOpen && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Colore app — prima */}
              <div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: 8 }}>{T.settings.themeColor}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {ACCENT_COLORS.map((c, i) => (
                    <button key={c} type="button" onClick={() => chooseAccent(c)}
                      aria-label={`${A.themeColor} ${i + 1}`} aria-pressed={accent === c}
                      className="tap-grow"
                      style={{ '--tap-x': '5px', width: 34, height: 34, borderRadius: '50%', border: accent === c ? '3px solid var(--color-text)' : '2px solid transparent', background: c, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.85rem', padding: 0 }}>
                      {accent === c ? '✓' : ''}
                    </button>
                  ))}
                  {/* Back to MeMo's own green — last, after the choosing. */}
                  <button type="button" onClick={() => chooseAccent('')} aria-label={`${A.themeColor} ${ACCENT_COLORS.length + 1}`} aria-pressed={accent === ''}
                    className="tap-grow"
                    style={{ '--tap-x': '5px', width: 34, height: 34, borderRadius: '50%', border: accent === '' ? '3px solid var(--color-text)' : '2px solid var(--color-border)', background: '#7A9E7E', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.85rem', padding: 0 }}>
                    {accent === '' ? '✓' : ''}
                  </button>
                </div>
              </div>

              {/* Chiaro · Scuro · Meteo — stessa fila, stessa grandezza */}
              <div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => handleThemeChange('light')}
                    style={{ ...themeButtonBase, flex: 1, minHeight: 48, padding: '12px 6px', background: '#F7F4EF', color: '#2C2A27', border: form.theme === 'light' ? '2px solid #7A9E7E' : '2px solid #C8C0B4', boxShadow: form.theme === 'light' ? '0 0 0 2px rgba(122,158,126,0.25)' : 'none' }}>
                    {T.settings.themeLight}
                  </button>
                  <button type="button" onClick={() => handleThemeChange('dark')}
                    style={{ ...themeButtonBase, flex: 1, minHeight: 48, padding: '12px 6px', background: '#252720', color: '#EDE9E1', border: form.theme === 'dark' ? '2px solid #4AA86A' : '2px solid #3A3D34', boxShadow: form.theme === 'dark' ? '0 0 0 2px rgba(74,168,106,0.2)' : 'none' }}>
                    {T.settings.themeDark}
                  </button>
                  <button type="button" onClick={weatherPerm === 'granted' ? disableWeather : activateWeather} disabled={weatherActivating}
                    style={{ ...themeButtonBase, flex: 1, minHeight: 48, padding: '12px 6px', whiteSpace: 'nowrap', background: weatherPerm === 'granted' ? 'var(--color-primary)' : 'var(--color-card)', color: weatherPerm === 'granted' ? '#fff' : 'var(--color-text)', border: `2px solid ${weatherPerm === 'granted' ? 'var(--color-primary)' : 'var(--color-border)'}` }}>
                    {weatherActivating ? (T.settings.weatherActivating || '…') : (weatherPerm === 'granted' ? ('✓ ' + (T.settings.weatherReset || 'Meteo')) : (T.settings.weatherReset || 'Meteo'))}
                  </button>
                </div>
                {/* Two wordings for one refusal, because the place to go and
                    undo it is not the same. Inside the installed app there is
                    no browser to open the settings of — sending somebody there
                    is sending them nowhere. */}
                {weatherError === 'denied' && (<p role="alert" style={{ fontSize: '0.75rem', color: '#e63946', marginTop: 8 }}>{(isNativeApp() ? T.settings.weatherDeniedHintApp : T.settings.weatherDeniedHint) || 'Accesso alla posizione negato.'}</p>)}
                {weatherError === 'unavailable' && (<p style={{ fontSize: '0.75rem', color: '#e63946', marginTop: 8 }}>{T.settings.weatherUnavailable || '—'}</p>)}
              </div>
              {/* Seconda fila, sotto Chiaro/Scuro: Perla del giorno (on/off) e
                  Voce di lettura (apre il pannello voce). Stessa dimensione. */}
              {(() => {
                const PL = {
                  it: { arch: 'Archivio', archEmpty: 'Ancora nessuna perla da rivedere.', t: 'Perla del giorno', d: 'Un pensiero al giorno in cima a Oggi e nel riepilogo del mattino.' },
                  en: { arch: 'Archive', archEmpty: 'No pearls to look back on yet.', t: 'Thought of the day', d: 'A thought a day at the top of Today and in your morning summary.' },
                  es: { arch: 'Archivo', archEmpty: 'Aún no hay perlas que revisar.', t: 'Pensamiento del día', d: 'Un pensamiento al día arriba en Hoy y en el resumen de la mañana.' },
                  de: { arch: 'Archiv', archEmpty: 'Noch keine Perlen zum Nachlesen.', t: 'Gedanke des Tages', d: 'Ein Gedanke pro Tag oben in Heute und in der Morgenübersicht.' },
                  fr: { arch: 'Archives', archEmpty: 'Aucune perle à revoir pour l’instant.', t: 'Pensée du jour', d: 'Une pensée par jour en haut d’Aujourd’hui et dans le résumé du matin.' },
                  nl: { arch: 'Archief', archEmpty: 'Nog geen parels om terug te lezen.', t: 'Gedachte van de dag', d: 'Elke dag een gedachte bovenaan Vandaag en in je ochtendoverzicht.' },
                  pt: { arch: 'Arquivo', archEmpty: 'Ainda não há pérolas para rever.', t: 'Pensamento do dia', d: 'Um pensamento por dia no topo de Hoje e no resumo da manhã.' },
                  ru: { arch: 'Архив', archEmpty: 'Пока нечего пересматривать.', t: 'Мысль дня', d: 'Одна мысль в день вверху раздела «Сегодня» и в утренней сводке.' },
                  pl: { arch: 'Archiwum', archEmpty: 'Nie ma jeszcze pereł do przejrzenia.', t: 'Myśl dnia', d: 'Jedna myśl dziennie na górze Dziś i w porannym podsumowaniu.' },
                  ro: { arch: 'Arhivă', archEmpty: 'Încă nu ai perle de revăzut.', t: 'Gândul zilei', d: 'Un gând pe zi în capul paginii Azi și în rezumatul de dimineață.' },
                  zh: { arch: '历史', archEmpty: '暂时还没有可回顾的句子。', t: '每日一言', d: '每天一句话，显示在“今天”顶部和早间摘要中。' },
                  ja: { arch: '履歴', archEmpty: 'まだ振り返る言葉がありません。', t: '今日の一言', d: '毎日ひとつの言葉を「今日」の上部と朝のまとめに表示します。' },
                  hi: { arch: 'संग्रह', archEmpty: 'अभी देखने के लिए कोई मोती नहीं।', t: 'आज का विचार', d: 'रोज़ एक विचार — "आज" के ऊपर और सुबह के सारांश में।' },
                  ko: { arch: '보관함', archEmpty: '아직 다시 볼 문장이 없습니다.', t: '오늘의 한마디', d: '하루 한 문장을 “오늘” 상단과 아침 요약에 보여줍니다.' },
                  ar: { arch: 'الأرشيف', archEmpty: 'لا توجد لآلئ لمراجعتها بعد.', t: 'حكمة اليوم', d: 'فكرة كل يوم أعلى صفحة اليوم وفي ملخّص الصباح.' },
                }[baseLang] || { arch: 'Archive', archEmpty: 'No pearls to look back on yet.', t: 'Thought of the day', d: 'A thought a day at the top of Today and in your morning summary.' };
                const on = pearlEnabled;
                // Same shape and size as Chiaro/Scuro/Meteo above, so the two
                // rows read as one block. Smaller type and wrapping allowed:
                // these labels are longer than "Chiaro" and must not overflow.
                const cell = { ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 48,
                  padding: '10px 4px', fontSize: '0.82rem', lineHeight: 1.15, textAlign: 'center' };
                const cellOn = (active) => ({ ...cell,
                  background: active ? 'var(--color-primary)' : 'var(--color-card)',
                  color: active ? '#fff' : 'var(--color-text)',
                  border: `2px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}` });
                return (
                  <div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={() => setVoiceMenuOpen(o => !o)} aria-expanded={voiceMenuOpen} style={cellOn(voiceMenuOpen)}>
                        {T.settings.voiceSection} {voiceMenuOpen ? '▲' : '▾'}
                      </button>
                      <button type="button" onClick={togglePearl} aria-pressed={on} style={cellOn(on)}>
                        {on ? '✓ ' : ''}{PL.t}
                      </button>
                      {/* The third place stays empty rather than letting these
                          two stretch to fill it. Every button in this section
                          is a third of a line wide; two halves here would make
                          the row below Chiaro/Scuro read as a different kind of
                          thing, which it is not. */}
                      <div style={{ flex: 1, minWidth: 0 }} aria-hidden="true" />
                    </div>
                  </div>
                );
              })()}
              {/* Pannello voce — si apre col pulsante "Voce di lettura" qui sopra */}
              {voiceMenuOpen && (
                <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
                  <VoicePicker lang={form.language} T={T} />
                </div>
              )}
            </div>
          )}
        </div>

        <Divider />

        {/* (Riga Collega Apple · Accessibilità · Utilizzo dati spostata sotto Notifiche) */}

        {/* ── Riga 4: Notifiche · Etichette · Riepilogo mattutino ──────────── */}
        <div style={{ padding: '18px 0' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            {[
              { key: 'notif',   open: notifMenuOpen,   set: () => { setNotifMenuOpen(o => !o); setLabelMenuOpen(false); setSummaryMenuOpen(false); }, icon: null, label: T.settings.alarmSection },
              { key: 'label',   open: labelMenuOpen,   set: () => { setLabelMenuOpen(o => !o); setAddingSubCal(true); setNotifMenuOpen(false); setSummaryMenuOpen(false); }, icon: null, label: T.settings.subCalendarsSection || 'Etichette' },
              { key: 'summary', open: summaryMenuOpen, set: () => { setSummaryMenuOpen(o => !o); setNotifMenuOpen(false); setLabelMenuOpen(false); }, icon: null, label: T.settings.summarySection },
            ].map(({ key, open, set, icon, label }) => (
              <button key={key} type="button" onClick={set} aria-expanded={open}
                style={{ ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'var(--color-card)', color: 'var(--color-text)', border: open ? '2px solid var(--color-primary)' : '2px solid var(--color-border)' }}>
                {icon && <span style={{ fontSize: '1.15rem', lineHeight: 1 }}>{icon}</span>}
                <span className="tile-label" style={{ textAlign: 'center' }}>{label} {open ? '▲' : '▾'}</span>
              </button>
            ))}
          </div>

          {labelMenuOpen && (<>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 12, marginBottom: subCalendars.length > 0 ? 10 : 0 }}>
            {/* A circle with a +, the same one whether you are opening the
                menu or already inside making a label. The wide green bar said
                its own name and then the form underneath said it again. */}
            {!addingSubCal && (
              <AddBtn onClick={() => setAddingSubCal(true)} label={T.settings.subCalendarAdd} />
            )}
            {/* The labels MeMo made by itself — one for every calendar that is
                not yours — do carry a colour, because a label with no colour
                would leave Jodi exactly where she started: "everything is one
                colour so events blend together". But the colour is a placeholder
                and this line says so, right where the labels are. */}
            {subCalendars.some(sc => sc.google_calendar_id && sc.google_calendar_id !== 'primary') && (
              <span style={{ flexBasis: '100%', fontSize: '0.72rem', color: 'var(--color-text-muted)', lineHeight: 1.35 }}>
                {T.settings.subCalendarAutoHint}
              </span>
            )}
            {subCalendars.map(sc => (
              <button
                key={sc.id}
                type="button"
                onClick={() => {
                  if (editingSCId === sc.id) { setEditingSCId(null); }
                  else { setEditingSCId(sc.id); setEditingSCData({ name: sc.name, color: sc.color }); }
                }}
                style={{
                  background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: 700, fontFamily: 'inherit',
                  textTransform: 'uppercase', letterSpacing: '0.04em', color: sc.color,
                  textDecoration: editingSCId === sc.id ? 'underline' : 'none', textUnderlineOffset: '3px',
                }}
              >
                {sc.name}
              </button>
            ))}
          </div>

          {/* Your own calendar can have a colour too.
              Everything that is NOT yours gets one by itself — you cannot
              label somebody else's events, so MeMo does it for you. Your own
              does not, because if every event is tinted the tint stops saying
              anything. But Jodi reads her week as "mine is pink, Wayne's is
              blue", and that is a fair thing to want: so it is a switch, off
              until asked. Once on it is an ordinary label, sitting in the row
              above, renameable and recolourable like the rest. */}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', cursor: ownBusy ? 'wait' : 'pointer', margin: '4px 0 12px' }}>
            <input
              type="checkbox"
              disabled={ownBusy}
              checked={subCalendars.some(sc => sc.google_calendar_id === 'primary')}
              onChange={async e => {
                const want = e.target.checked;
                // Turning it ON no longer creates anything yet: it opens the
                // colours and waits. We went from fuchsia to blue and Silvia's
                // answer was that neither is ours to pick — "voglio che sia di
                // libera scelta". Turning it OFF is immediate; there is nothing
                // to choose about removing something.
                if (want) { setOwnPicking(true); return; }
                setOwnBusy(true);
                try {
                  const made = await setOwnColour(want);
                  // Turning it on opens the colour straight away. Silvia: "voglio
                  // poter decidere il colore e non dover usare per forza il rosa".
                  // The label was always editable in the row above — but a colour
                  // you have to go and find is a colour you were given.
                  if (want && made?.id) {
                    setEditingSCId(made.id);
                    setEditingSCData({ name: made.name || '', color: made.color || '#7fa9d9' });
                  } else if (!want) {
                    setEditingSCId(null);
                  }
                } catch (_) { /* the box goes back on its own */ }
                setOwnBusy(false);
              }}
              style={{ marginTop: 3, flexShrink: 0 }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--color-text)' }}>{T.settings.subCalendarOwn}</span>
          </label>

          {/* The colours, waiting. Picking one is what creates the label —
              there is no "save", because the tap IS the decision. */}
          {ownPicking && (
            <div style={{
              border: '1.5px solid var(--color-border)', borderRadius: 10,
              padding: 12, marginBottom: 12,
            }}>
              <ColorPicker lang={form.language} value={null} onChange={async (c) => {
                setOwnBusy(true);
                try {
                  const made = await setOwnColour(true, c);
                  if (made?.id) {
                    setEditingSCId(made.id);
                    setEditingSCData({ name: made.name || '', color: made.color || c });
                  }
                } catch (_) { /* the box goes back on its own */ }
                setOwnPicking(false);
                setOwnBusy(false);
              }} />
              <ActionRow style={{ marginTop: 10 }}>
                <BackBtn label={A.close} onClick={() => setOwnPicking(false)} />
              </ActionRow>
            </div>
          )}

          {/* Inline edit panel — opens when a label is clicked */}
          {editingSCId && (
            <div style={{
              background: 'var(--color-surface)',
              border: `2px solid ${editingSCData.color || 'var(--color-border)'}`,
              borderRadius: 10,
              padding: '14px',
              marginBottom: 10,
            }}>
              <input
                className="form-input"
                value={editingSCData.name || ''}
                onChange={e => setEditingSCData(d => ({ ...d, name: e.target.value }))}
                style={{ marginBottom: 10 }}
              />
              <ColorPicker lang={form.language} value={editingSCData.color} onChange={c => setEditingSCData(d => ({ ...d, color: c }))} />
              {/* OK · back · bin — the same three, in the same order, in the
                  same shapes as everywhere else. See RoundBtn.jsx. */}
              <ActionRow>
                <OkBtn
                  label={A.renameSave}
                  onClick={async () => {
                    setSubCalError(false);
                    try {
                      await updateSubCalendar(editingSCId, editingSCData);
                      setEditingSCId(null);
                    } catch (_) { setSubCalError(true); }
                  }}
                />
                <BackBtn label={A.renameCancel} onClick={() => setEditingSCId(null)} />
                <Spacer />
                <DeleteBtn
                  label={A.deleteLabel}
                  onClick={async () => {
                    if (window.confirm(T.settings.subCalendarDeleteConfirm)) {
                      setSubCalError(false);
                      try {
                        await deleteSubCalendar(editingSCId);
                        setEditingSCId(null);
                      } catch (_) { setSubCalError(true); }
                    }
                  }}
                />
              </ActionRow>
            </div>
          )}

          {/* Add new label form (button lives in the section header row) */}
          {addingSubCal && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              <input className="form-input" placeholder={T.settings.subCalendarName}
                value={newSC.name} onChange={e => setNewSC(d => ({ ...d, name: e.target.value }))}
                style={{ marginBottom: 0 }} />
              <ColorPicker lang={form.language} value={newSC.color} onChange={c => setNewSC(d => ({ ...d, color: c }))} />
              {/* The same three again. "Scegli un colore" is gone at Silvia's
                  request: OK is plainly unavailable until a name and a colour
                  exist, and a line of instructions under a row of colours is a
                  line nobody reads twice. The bin here throws away what you
                  have just typed — it is the only thing there is to delete
                  before the label exists. */}
              <ActionRow style={{ marginTop: 4 }}>
                <OkBtn
                  label={T.settings.subCalendarAdd}
                  disabled={!newSC.name.trim() || !newSC.color}
                  onClick={async () => {
                    if (!newSC.name.trim() || !newSC.color) return;
                    setSubCalError(false);
                    try {
                      await createSubCalendar({ ...newSC, position: subCalendars.length });
                      setNewSC({ name: '', emoji: '', color: '' });
                      setAddingSubCal(false);
                    } catch (_) { setSubCalError(true); }
                  }}
                />
                <BackBtn label={A.close} onClick={() => setAddingSubCal(false)} />
                <Spacer />
                <DeleteBtn
                  label={A.close}
                  disabled={!newSC.name.trim() && !newSC.color}
                  onClick={() => { setNewSC({ name: '', emoji: '', color: '' }); setAddingSubCal(false); }}
                />
              </ActionRow>
            </div>
          )}
          {subCalError && (
            <p style={{ fontSize: '0.78rem', color: '#e63946', marginTop: 8 }}>
              {T.settings.saveError || T.today?.error || 'Errore — riprova.'}
            </p>
          )}
          </>)}
        </div>

        {/* Notifiche panel (button is in Row 4 above) */}
        <div style={{ padding: 0 }}>
          {notifMenuOpen && (<>
          {/* Does this phone let a reminder appear at all? Loraine set one, was
              shown a pop-up inside MeMo, and got nothing anywhere else — the app
              had accepted a promise it could not keep, and said nothing. The
              cause differs between the installed app and a browser; the
              question does not, so it is asked once and answered plainly here.
              Green when it is fine, red when it is not: this is the one line on
              the page that says whether the alarms work. */}
          {notifState && notifState !== 'ok' && (() => {
            const NW = NOTIFY_WORDS[form.language] || NOTIFY_WORDS[String(form.language).split('-')[0]] || NOTIFY_WORDS.en;
            return (
              <div style={{
                marginTop: 12, padding: '10px 12px', borderRadius: 10,
                border: '1.5px solid #e6b0aa', background: 'rgba(192,57,43,0.06)',
                fontSize: '0.78rem', lineHeight: 1.4, color: 'var(--color-text)',
              }}>
                <div>{NW[notifState]}</div>
                {notifState === 'ask' && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    style={{ marginTop: 8 }}
                    onClick={async () => setNotifState(await askNotifyPermission())}
                  >
                    {NW.enable}
                  </button>
                )}
              </div>
            );
          })()}
          <div className="notif-green" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
            {(() => {
              const NL = {
                it:{recv:'Ricevi notifiche',he:'Quanto prima',yes:'Sì',no:'No',at:"All'inizio",min:'min',h:'ora',hh:'ore',d:'giorno'},
                en:{recv:'Notifications',he:'How early',yes:'Yes',no:'No',at:'At start',min:'min',h:'hour',hh:'hours',d:'day'},
                es:{recv:'Recibir notificaciones',he:'Cuánto antes',yes:'Sí',no:'No',at:'Al inicio',min:'min',h:'hora',hh:'horas',d:'día'},
                de:{recv:'Benachrichtigungen',he:'Wie früh',yes:'Ja',no:'Nein',at:'Zu Beginn',min:'Min',h:'Std',hh:'Std',d:'Tag'},
                fr:{recv:'Notifications',he:'Combien avant',yes:'Oui',no:'Non',at:'Au début',min:'min',h:'heure',hh:'heures',d:'jour'},
                nl:{recv:'Meldingen',he:'Hoe vroeg',yes:'Ja',no:'Nee',at:'Bij aanvang',min:'min',h:'uur',hh:'uur',d:'dag'},
                pt:{recv:'Notificações',he:'Com quanto tempo',yes:'Sim',no:'Não',at:'No início',min:'min',h:'hora',hh:'horas',d:'dia'},
                ru:{recv:'Уведомления',he:'За сколько',yes:'Да',no:'Нет',at:'В начале',min:'мин',h:'ч',hh:'ч',d:'день'},
                pl:{recv:'Powiadomienia',he:'Z jakim wyprzedzeniem',yes:'Tak',no:'Nie',at:'Na początku',min:'min',h:'godz',hh:'godz',d:'dzień'},
                ro:{recv:'Notificări',he:'Cu cât timp înainte',yes:'Da',no:'Nu',at:'La început',min:'min',h:'oră',hh:'ore',d:'zi'},
                zh:{recv:'接收通知',he:'提前多久',yes:'是',no:'否',at:'开始时',min:'分钟',h:'小时',hh:'小时',d:'天'},
                ja:{recv:'通知を受け取る',he:'どれくらい前',yes:'はい',no:'いいえ',at:'開始時',min:'分',h:'時間',hh:'時間',d:'日'},
                hi:{recv:'सूचनाएँ',he:'कितना पहले',yes:'हाँ',no:'नहीं',at:'शुरुआत में',min:'मिनट',h:'घंटा',hh:'घंटे',d:'दिन'},
                ko:{recv:'알림 받기',he:'얼마나 미리',yes:'예',no:'아니요',at:'시작 시',min:'분',h:'시간',hh:'시간',d:'일'},
                ar:{recv:'الإشعارات',he:'قبل كم من الوقت',yes:'نعم',no:'لا',at:'عند البدء',min:'دقيقة',h:'ساعة',hh:'ساعات',d:'يوم'},
              };
              const L = NL[baseLang] || NL.en;
              // How a new reminder behaves: rings, or stays a silent note.
              const TYPE_TBL = {
                it: { label: 'Come avvisa',     alarm: '🔔 Suona',          message: '✉️ In silenzio' },
                en: { label: 'How it warns you', alarm: '🔔 Rings',         message: '✉️ Silent' },
                es: { label: 'Cómo avisa',      alarm: '🔔 Suena',          message: '✉️ En silencio' },
                de: { label: 'Art der Meldung', alarm: '🔔 Klingelt',       message: '✉️ Lautlos' },
                fr: { label: 'Type d’alerte',   alarm: '🔔 Sonne',          message: '✉️ Silencieux' },
                nl: { label: 'Hoe het meldt',   alarm: '🔔 Belt',           message: '✉️ Stil' },
                pt: { label: 'Como avisa',      alarm: '🔔 Toca',           message: '✉️ Em silêncio' },
                ru: { label: 'Как напоминать',  alarm: '🔔 Со звуком',      message: '✉️ Без звука' },
                pl: { label: 'Jak przypomina',  alarm: '🔔 Dzwoni',         message: '✉️ Po cichu' },
                ro: { label: 'Cum anunță',      alarm: '🔔 Sună',           message: '✉️ Silențios' },
                zh: { label: '提醒方式',          alarm: '🔔 响铃',            message: '✉️ 静音' },
                ja: { label: '知らせ方',          alarm: '🔔 鳴らす',           message: '✉️ 音なし' },
                hi: { label: 'कैसे बताए',         alarm: '🔔 बजे',             message: '✉️ चुपचाप' },
                ko: { label: '알림 방식',         alarm: '🔔 소리',            message: '✉️ 무음' },
                ar: { label: 'طريقة التنبيه',   alarm: '🔔 يرن',            message: '✉️ صامت' },
              };
              const TYPE_L = TYPE_TBL[baseLang] || TYPE_TBL.en;
              const fmt = v => v === 0 ? L.at : v < 60 ? `${v} ${L.min}` : v === 60 ? `1 ${L.h}` : v === 1440 ? `1 ${L.d}` : `${v / 60} ${L.hh}`;
              const cell = { flex: '1 1 45%', minWidth: 130, fontSize: '0.92rem', color: 'var(--color-text-muted)' };
              return (
                <>
                  {/* Ricevi notifiche */}
                  <label style={cell}>
                    {L.recv}
                    <select className="form-select" style={{ marginTop: 4 }} value={notifEnabled ? '1' : '0'}
                      onChange={e => { const on = e.target.value === '1'; setNotifEnabled(on); localStorage.setItem(NOTIF_KEYS.enabled, on ? '1' : '0'); }}>
                      <option value="1">{L.yes}</option>
                      <option value="0">{L.no}</option>
                    </select>
                  </label>
                  {/* Quanto prima */}
                  <label style={cell}>
                    {L.he}
                    <select className="form-select" style={{ marginTop: 4 }} value={notifDefaultMin}
                      onChange={e => { const m = parseInt(e.target.value, 10); setNotifDefaultMin(m); localStorage.setItem(NOTIF_KEYS.defaultMin, String(m)); }}>
                      {[0, 5, 10, 15, 30, 60, 120, 180, 360, 720, 1440].map(v => <option key={v} value={v}>{fmt(v)}</option>)}
                    </select>
                  </label>
                  {/* Come avvisa: suona, oppure resta una nota silenziosa.
                      New reminders ring by default — the promise on the store
                      page is an alarm — and this is where somebody who would
                      rather not be startled turns that off once and for all,
                      instead of tapping the bell on every single event. */}
                  <label style={cell}>
                    {TYPE_L.label}
                    <select className="form-select" style={{ marginTop: 4 }} value={notifDefaultType}
                      onChange={e => {
                        const v = e.target.value === 'message' ? 'message' : 'alarm';
                        setNotifDefaultType(v);
                        localStorage.setItem(NOTIF_KEYS.defaultType, v);
                      }}>
                      <option value="alarm">{TYPE_L.alarm}</option>
                      <option value="message">{TYPE_L.message}</option>
                    </select>
                  </label>
                </>
              );
            })()}
            {/* Volume */}
            <label style={{ flex: '0 1 calc(50% - 5px)', minWidth: 130, fontSize: '0.92rem', color: 'var(--color-text-muted)' }}>
              {T.settings.alarmVolume}
              <select className="form-select" style={{ marginTop: 4 }}
                value={[0.2, 0.4, 0.6, 0.8, 1].reduce((a, b) => Math.abs(b - alarmVolume) < Math.abs(a - alarmVolume) ? b : a, 1)}
                onChange={e => { const v = parseFloat(e.target.value); setAlarmVolume(v); localStorage.setItem(ALARM_KEYS.volume, v.toString()); }}>
                {[20, 40, 60, 80, 100].map(p => <option key={p} value={p / 100}>{p}%</option>)}
              </select>
            </label>

            {/* Durata posticipo */}
            <label style={{ flex: '0 1 calc(50% - 5px)', minWidth: 130, fontSize: '0.92rem', color: 'var(--color-text-muted)' }}>
              {T.settings.alarmSnooze}
              <select className="form-select" style={{ marginTop: 4 }}
                value={alarmSnooze}
                onChange={e => { const m = parseInt(e.target.value, 10); setAlarmSnooze(m); localStorage.setItem(ALARM_KEYS.snoozeMin, m.toString()); }}>
                {[5, 10, 15, 30].map(m => <option key={m} value={m}>{m} {T.settings.alarmMin}</option>)}
              </select>
            </label>

            {/* Max posticipo */}
            <label style={{ flex: '0 1 calc(50% - 5px)', minWidth: 130, fontSize: '0.92rem', color: 'var(--color-text-muted)' }}>
              {T.settings.alarmMaxSnooze}
              <select className="form-select" style={{ marginTop: 4 }}
                value={alarmMaxSnooze}
                onChange={e => { const n = parseInt(e.target.value, 10); setAlarmMaxSnooze(n); localStorage.setItem(ALARM_KEYS.maxSnooze, n.toString()); }}>
                {[3, 5].map(n => <option key={n} value={n}>{n}×</option>)}
                <option value={SNOOZE_FOREVER}>∞ {T.settings.alarmForever}</option>
              </select>
            </label>

            {/* Suono — preview plays on change */}
            <label style={{ flex: '0 1 calc(50% - 5px)', minWidth: 130, fontSize: '0.92rem', color: 'var(--color-text-muted)' }}>
              {T.settings.alarmSound || 'Suono'}
              <select className="form-select" style={{ marginTop: 4 }}
                value={alarmSound}
                onChange={e => { const v = e.target.value; setAlarmSound(v); localStorage.setItem(ALARM_KEYS.sound, v); playAlertSound(v); }}>
                {ALERT_SOUNDS.map(s => <option key={s.value} value={s.value}>{s.name}</option>)}
              </select>
            </label>
          </div>

          {/* Notifiche push — arrivano al telefono anche ad app chiusa */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: '0.92rem', color: 'var(--color-text-muted)', marginBottom: 8 }}>
              {T.settings.pushIntro}
            </div>
            <button type="button" className="btn btn-outline btn-sm" disabled={testingPush}
              onClick={async () => {
                setTestingPush(true); setPushTestResult(null);
                try {
                  // In the installed app this button was testing the WRONG
                  // thing. An Android WebView has no Web Push at all, so the
                  // test could only ever fail, and it answered with a red
                  // paragraph that read like an error — Loraine: "this is what
                  // pops up for the notification test. I had already allowed
                  // notifications in my phone setting but I am not getting
                  // them." She had done everything right and was being shown a
                  // failure for a road the app does not even use there.
                  //
                  // The installed app's reminders come from Android itself, so
                  // the test does what a reminder does: schedule one, two
                  // seconds out. If it appears, the whole chain works — and if
                  // the permission is missing this is also where it gets asked.
                  if (isNativeApp()) {
                    const { LocalNotifications } = await import('@capacitor/local-notifications');
                    const perm = await LocalNotifications.checkPermissions();
                    if (perm.display !== 'granted') {
                      const asked = await LocalNotifications.requestPermissions();
                      if (asked.display !== 'granted') throw new Error('permission_denied');
                    }
                    await LocalNotifications.schedule({
                      notifications: [{
                        id: 999001,
                        title: 'MeMo',
                        body: T.settings.pushSent,
                        schedule: { at: new Date(Date.now() + 2000) },
                      }],
                    });
                    setPushTestResult({ ok: true });
                    setTestingPush(false);
                    return;
                  }
                  await subscribeToPush();           // ask permission + register this device (user gesture)
                  const r = await apiClient.post('/push/test'); // then send the test notification
                  if (r.data && r.data.sent === 0) {
                    setPushTestResult({ ok: false, error: T.settings.pushNoDevice });
                  } else {
                    setPushTestResult({ ok: true });
                  }
                } catch (err) {
                  const code = err.message || err.response?.data?.error || null;
                  const msg = code === 'permission_denied'
                    ? T.settings.pushDenied
                    : code === 'push_unsupported'
                    // Two different truths behind one missing API. In a
                    // browser it means the browser is too old or too odd. In
                    // the installed app it means something permanent: an
                    // Android WebView has no Push and no Notification at all,
                    // so this can never work there — and the old wording sent
                    // an Android user instructions about an iPhone.
                    ? (isNativeApp() ? T.settings.pushUnsupportedApp : T.settings.pushUnsupported)
                    : T.settings.pushFailed;
                  setPushTestResult({ ok: false, error: msg });
                } finally { setTestingPush(false); }
              }}>
              {testingPush ? T.settings.pushSending : T.settings.pushTestButton}
            </button>
            {pushTestResult && (
              <p style={{ fontSize: '0.78rem', marginTop: 6, color: pushTestResult.ok ? 'var(--color-primary)' : '#e63946' }}>
                {pushTestResult.ok ? T.settings.pushSent : (pushTestResult.error || T.settings.pushError)}
              </p>
            )}
          </div>
          </>)}
        </div>

        {/* Riepilogo panel (button is in Row 4 above; .ics moved to Row 5) */}
        <div style={{ padding: 0 }}>
          {summaryMenuOpen && (<div style={{ marginTop: 12 }}>
          {/* The server only sends a summary to somebody who has an email or a
              Telegram chat: the query that finds who is due filters on exactly
              those two columns. With neither, nothing happens and nothing is
              said — Loraine set a time, waited for a morning, and checked her
              spam folder for an email that was never going to be sent.
              A promise the app cannot keep should be visible where the promise
              is made. */}
          {!form.summary_email && !form.telegram_chat_id && (
            <p role="status" style={{
              margin: '0 0 12px', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
              border: '1.5px solid #e6c200', background: '#fffbe6',
              fontSize: '0.8rem', lineHeight: 1.45, color: '#6b5800',
            }}>
              {T.settings.summaryNowhere}
            </p>
          )}
          <SectionRow label={T.settings.summarySection}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--color-primary-dark)', fontWeight: 700, flexShrink: 0 }}>{T.settings.sendTime}</span>
              <TimeField
                value={form.summary_time || '07:00'}
                onChange={t => setForm(f => ({ ...f, summary_time: t }))}
                hour12={uses12h(user?.language)}
              />
            </div>
          </SectionRow>

          {/* Only when there is something to say — applies to BOTH channels
              (Telegram and email), so it sits with the send time, not with
              either channel's own block. Off by default: the summary always
              arrives unless the person asks for silence on empty days. */}
          {(() => {
            const SE = SKIP_EMPTY_L[baseLang] || SKIP_EMPTY_L.en;
            return (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', margin: '2px 0 14px' }}>
                <input type="checkbox" checked={skipEmptySummary} onChange={toggleSkipEmptySummary} style={{ marginTop: 3, flexShrink: 0 }} />
                <span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--color-text)' }}>{SE.label}</span>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.35 }}>{SE.hint}</span>
                </span>
              </label>
            );
          })()}


          {/* Telegram — checkbox + "how to connect" dropdown, bottom-aligned */}
          <div className="summary-channel-row" style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
            <label className="summary-channel-toggle">
              <input type="checkbox" checked={notifyTelegram}
                onChange={e => {
                  setNotifyTelegram(e.target.checked);
                  if (!e.target.checked) setForm(f => ({ ...f, telegram_chat_id: '' }));
                }} />
              <span className="summary-channel-label">
                <span style={{ fontSize: '1.1rem' }}>📨</span>
                {T.settings.viaTelegram}
              </span>
            </label>
            {notifyTelegram && botUsername && (
              <details>
                <summary style={{ cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-primary-dark)', userSelect: 'none' }}>
                  {T.settings.telegramSetupTitle}
                </summary>
                <div style={{ background: 'var(--color-surface)', borderRadius: 8, padding: '8px 12px', marginTop: 6, fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                  <ol style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.7 }}>
                    <li>{T.settings.telegramStep1(botUsername)}</li>
                    <li>{T.settings.telegramStep2}</li>
                    <li>{T.settings.telegramStep3}</li>
                    <li>{T.settings.telegramStep4}</li>
                  </ol>
                </div>
              </details>
            )}
          </div>
          {notifyTelegram && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, marginBottom: 10, paddingInlineStart: 28 }}>
              <input className="form-input" name="telegram_chat_id" value={form.telegram_chat_id}
                onChange={handleChange} placeholder={T.settings.chatIdPlaceholder}
                style={{ width: 150, flexShrink: 0, padding: '6px 10px' }} />
              <button type="button" className="btn btn-outline btn-sm" onClick={handleTestTelegram} disabled={testingTelegram}
                style={{ marginInlineStart: 'auto' }}>
                {testingTelegram ? T.settings.testSending : T.settings.testButton}
              </button>
              {telegramResult && (
                <p style={{ fontSize: '0.78rem', margin: 0, width: '100%', color: telegramResult.ok ? 'var(--color-green)' : 'var(--color-red)' }}>
                  {telegramResult.ok ? T.settings.testSuccess : (telegramResult.apiError || T.settings.testError)}
                </p>
              )}
              {/* The morning summary always arrives on Telegram; the per-event
                  reminders are a separate choice — on the phone the app already
                  rings by itself, so repeating them in the chat is often noise. */}
              {(() => {
                const TA = TG_ALERTS_L[baseLang] || TG_ALERTS_L.en;
                return (
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', cursor: 'pointer', marginTop: 2 }}>
                    <input type="checkbox" checked={telegramAlerts} onChange={toggleTelegramAlerts} style={{ marginTop: 3, flexShrink: 0 }} />
                    <span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text)' }}>{TA.label}</span>
                      <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.35 }}>{TA.hint}</span>
                    </span>
                  </label>
                );
              })()}
            </div>
          )}

          {/* Email — test button sits at the end of the label line (right-aligned) */}
          <div className="summary-channel-row" style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label className="summary-channel-toggle">
              <input type="checkbox" checked={notifyEmail}
                onChange={e => {
                  setNotifyEmail(e.target.checked);
                  if (!e.target.checked) setForm(f => ({ ...f, summary_email: '' }));
                }} />
              <span className="summary-channel-label">
                <span style={{ fontSize: '1.1rem' }}>📧</span>
                <span>
                  {T.settings.viaEmail}
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', fontWeight: 400, marginInlineStart: 4 }}>
                    — Gmail, Outlook, iCloud…
                  </span>
                </span>
              </span>
            </label>
            {notifyEmail && form.summary_email && (
              <button type="button" className="btn btn-outline btn-sm" onClick={handleTestEmail} disabled={testingEmail}
                style={{ marginInlineStart: 'auto', flexShrink: 0 }}>
                {testingEmail ? T.settings.emailTestSending : T.settings.emailTestButton}
              </button>
            )}
          </div>
          {notifyEmail && (
            <div style={{ paddingInlineStart: 28, marginBottom: 4 }}>
              <input className="form-input" type="email" name="summary_email"
                value={form.summary_email || ''} onChange={handleChange}
                placeholder="email@example.com" style={{ marginBottom: 6, padding: '6px 10px' }} />
              {emailTestResult && (
                <p style={{ marginTop: 6, fontSize: '0.78rem', color: emailTestResult.ok ? 'var(--color-primary)' : '#e63946' }}>
                  {/* The plain sentence first, the provider's own words second and
                      smaller. Loraine read "add the new IP address in this link"
                      and went to a stranger's account to try to fix it herself.
                      The detail has to stay — it is what told us what was wrong —
                      but it is for whoever is sent it, not for her to act on. */}
                  {emailTestResult.ok ? T.settings.emailTestSuccess : (
                    <>
                      {T.settings.emailTestError}
                      {emailTestResult.error && (
                        <span style={{ display: 'block', marginTop: 4, fontSize: '0.68rem', opacity: 0.75, wordBreak: 'break-word' }}>
                          {emailTestResult.error}
                        </span>
                      )}
                    </>
                  )}
                </p>
              )}
            </div>
          )}
          </div>)}
        </div>

        {/* Push notification test moved up into the unified notification section */}

        <Divider />

        {/* ── Riga 3: Calendari · Accessibilità · Utilizzo dati ── */}
        <div style={{ padding: '18px 0' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            {[
              // Shared calendars used to be one of the five things along the
              // bottom of the screen, beside Today and Search — a whole
              // section for something most people set up once and then leave
              // alone. It is a place you go to, not a place you are, so it
              // moved here and the bottom bar went back to four.
              // No drawing: the words carry it, as on every other button here.
              // And "Calendari" named a place; "Condividi calendario" names
              // what you go there to do, which is the only reason anybody
              // opens it.
              { key: 'groups', go: '/groups', icon: null, label: ({ it: 'Condividi calendario', en: 'Share a calendar', es: 'Compartir calendario', de: 'Kalender teilen', fr: 'Partager un agenda', nl: 'Agenda delen', pt: 'Partilhar calendário', ru: 'Общий календарь', pl: 'Udostępnij kalendarz', ro: 'Partajează calendarul', zh: '共享日历', ja: 'カレンダーを共有', hi: 'कैलेंडर साझा करें', ko: '캘린더 공유', ar: 'مشاركة تقويم' }[baseLang] || 'Share a calendar') },
              { key: 'a11y',    open: a11yMenuOpen,    set: () => { setA11yMenuOpen(o => !o); setAppleMenuOpen(false); setUsageMenuOpen(false); }, icon: null, label: T.settings.a11yTab },
              { key: 'usage',   open: usageMenuOpen,   set: () => { setUsageMenuOpen(o => !o); setAppleMenuOpen(false); setA11yMenuOpen(false); }, icon: null, label: T.settings.usageTab },
            ].map(({ key, open, set, icon, label, go }) => (
              <button key={key} type="button" onClick={go ? () => navigate(go) : set} aria-expanded={go ? undefined : open}
                style={{
                  ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                  background: 'var(--color-card)', color: 'var(--color-text)',
                  border: open ? '2px solid var(--color-primary)' : '2px solid var(--color-border)',
                }}>
                {icon && <span style={{ fontSize: '1.15rem', lineHeight: 1 }}>{icon}</span>}
                <span className="tile-label" style={{ textAlign: 'center' }}>{label} {open ? '▲' : '▾'}</span>
              </button>
            ))}
          </div>

          {/* Utilizzo voce e foto */}
          {usageMenuOpen && (
            <div style={{ marginTop: 12 }}>
              {aiUsage ? (
                <div style={{ background: 'var(--color-card)', border: '2px solid var(--color-primary)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--color-text)' }}>{ul.title}</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 800, color: !usageUnlimited && usageCount >= usageLimit ? '#c0392b' : 'var(--color-primary-dark)' }}>
                      {usageUnlimited ? `${usageCount} · ∞` : `${usageCount}/${usageLimit}`}
                    </span>
                  </div>
                  {!usageUnlimited && (
                    <div style={{ height: 6, borderRadius: 6, background: 'var(--color-border)', marginTop: 6, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: usagePct + '%', background: usageCount >= usageLimit ? '#c0392b' : 'var(--color-primary)', transition: 'width 0.3s' }} />
                    </div>
                  )}
                  {!usageUnlimited && usageCount >= usageLimit * 0.7 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-primary-dark)', fontWeight: 700, marginTop: 6 }}>⭐ {ul.nudge}</div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>—</div>
              )}

              {/* Piani — mostra i pulsanti Abbonati se l'utente NON è già premium */}
              {(() => {
                const plan = (user?.plan || '').toLowerCase();
                const isPremium = plan === 'premium' || plan === 'family' || plan === 'lifetime';
                if (isPremium) return null;
                const PT = {
                  it: { choose: 'Scegli il tuo piano', p: 'Personal', pr: 'Premium', pd: '1 calendario condiviso · voce e foto illimitate', prd: 'Fino a 4 calendari (5 persone) · allegati più grandi · supporto prioritario', yr: '/anno', sub: 'Abbonati', note: 'Pagamento sicuro su Lemon Squeezy.' },
                  en: { choose: 'Choose your plan', p: 'Personal', pr: 'Premium', pd: '1 shared calendar · unlimited voice & photo', prd: 'Up to 4 calendars (5 people) · bigger attachments · priority support', yr: '/year', sub: 'Subscribe', note: 'Secure payment on Lemon Squeezy.' },
                  es: { choose: 'Elige tu plan', p: 'Personal', pr: 'Premium', pd: '1 calendario compartido · voz y foto ilimitadas', prd: 'Hasta 4 calendarios (5 personas) · adjuntos más grandes · soporte prioritario', yr: '/año', sub: 'Suscríbete', note: 'Pago seguro en Lemon Squeezy.' },
                  de: { choose: 'Wähle deinen Tarif', p: 'Personal', pr: 'Premium', pd: '1 geteilter Kalender · unbegrenzt Sprache & Fotos', prd: 'Bis zu 4 Kalender (5 Personen) · größere Anhänge · Priority-Support', yr: '/Jahr', sub: 'Abonnieren', note: 'Sichere Zahlung über Lemon Squeezy.' },
                  fr: { choose: 'Choisissez votre offre', p: 'Personal', pr: 'Premium', pd: '1 agenda partagé · voix et photo illimitées', prd: "Jusqu'à 4 agendas (5 personnes) · pièces jointes plus grandes · support prioritaire", yr: '/an', sub: "S'abonner", note: 'Paiement sécurisé sur Lemon Squeezy.' },
                  pt: { choose: 'Escolhe o teu plano', p: 'Personal', pr: 'Premium', pd: '1 calendário partilhado · voz e foto ilimitadas', prd: 'Até 4 calendários (5 pessoas) · anexos maiores · suporte prioritário', yr: '/ano', sub: 'Subscrever', note: 'Pagamento seguro na Lemon Squeezy.' },
                  nl: { choose: 'Kies je abonnement', p: 'Personal', pr: 'Premium', pd: '1 gedeelde agenda · onbeperkt spraak & foto', prd: 'Tot 4 agenda’s (5 personen) · grotere bijlagen · voorrangssupport', yr: '/jaar', sub: 'Abonneren', note: 'Veilig betalen via Lemon Squeezy.' },
                  ru: { choose: 'Выберите тариф', p: 'Personal', pr: 'Premium', pd: '1 общий календарь · голос и фото без лимита', prd: 'До 4 календарей (5 человек) · большие вложения · приоритетная поддержка', yr: '/год', sub: 'Оформить', note: 'Безопасная оплата в Lemon Squeezy.' },
                  pl: { choose: 'Wybierz plan', p: 'Personal', pr: 'Premium', pd: '1 wspólny kalendarz · głos i zdjęcia bez limitu', prd: 'Do 4 kalendarzy (5 osób) · większe załączniki · priorytetowe wsparcie', yr: '/rok', sub: 'Subskrybuj', note: 'Bezpieczna płatność w Lemon Squeezy.' },
                  ro: { choose: 'Alege planul', p: 'Personal', pr: 'Premium', pd: '1 calendar partajat · voce și foto nelimitate', prd: 'Până la 4 calendare (5 persoane) · atașamente mai mari · suport prioritar', yr: '/an', sub: 'Abonează-te', note: 'Plată sigură pe Lemon Squeezy.' },
                  zh: { choose: '选择你的套餐', p: 'Personal', pr: 'Premium', pd: '1 个共享日历 · 无限语音和照片', prd: '最多 4 个日历（5 人）· 更大附件 · 优先支持', yr: '/年', sub: '订阅', note: '通过 Lemon Squeezy 安全支付。' },
                  ja: { choose: 'プランを選択', p: 'Personal', pr: 'Premium', pd: '共有カレンダー1個 · 音声と写真が無制限', prd: '最大4カレンダー（5人）· 大きな添付 · 優先サポート', yr: '/年', sub: '登録する', note: 'Lemon Squeezy で安全に支払い。' },
                  hi: { choose: 'अपना प्लान चुनें', p: 'Personal', pr: 'Premium', pd: '1 साझा कैलेंडर · असीमित आवाज़ और फ़ोटो', prd: '4 कैलेंडर तक (5 लोग) · बड़े अटैचमेंट · प्राथमिकता सहायता', yr: '/वर्ष', sub: 'सब्सक्राइब', note: 'Lemon Squeezy पर सुरक्षित भुगतान।' },
                  ko: { choose: '요금제 선택', p: 'Personal', pr: 'Premium', pd: '공유 캘린더 1개 · 음성·사진 무제한', prd: '최대 4개 캘린더(5명) · 더 큰 첨부 · 우선 지원', yr: '/년', sub: '구독하기', note: 'Lemon Squeezy 안전 결제.' },
                  ar: { choose: 'اختر باقتك', p: 'Personal', pr: 'Premium', pd: 'تقويم مشترك واحد · صوت وصور بلا حدود', prd: 'حتى 4 تقاويم (5 أشخاص) · مرفقات أكبر · دعم ذو أولوية', yr: '/سنة', sub: 'اشترك', note: 'دفع آمن عبر Lemon Squeezy.' },
                }[baseLang] || null;
                const L = PT || { choose: 'Choose your plan', p: 'Personal', pr: 'Premium', pd: '1 shared calendar · unlimited voice & photo', prd: 'Up to 4 calendars (5 people) · bigger attachments · priority support', yr: '/year', sub: 'Subscribe', note: 'Secure payment on Lemon Squeezy.' };
                // Inside the Play Store app this whole block stays hidden:
                // prices, plans and the way to buy. See utils/platform.js for
                // why — in short, an app on Play may not send its users to an
                // outside checkout, and breaking that rule gets the app removed
                // once it is live. Whoever subscribed on the website finds
                // their features unlocked here just the same.
                if (!canShowPurchase()) return null;
                const card = { flex: 1, minWidth: 150, border: '2px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', background: 'var(--color-card)', display: 'flex', flexDirection: 'column', gap: 6 };
                const btn = (bg) => ({ marginTop: 'auto', textAlign: 'center', padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: bg, color: '#fff', fontWeight: 800, fontSize: '0.85rem', textDecoration: 'none' });
                return (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 8 }}>{L.choose}</div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div style={card}>
                        <div style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--color-text)' }}>{L.p} <span style={{ color: 'var(--color-primary-dark)' }}>€9,90{L.yr}</span></div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>{L.pd}</div>
                        <a href={buildCheckoutUrl('personal', user)} target="_blank" rel="noopener noreferrer" style={btn('var(--color-primary)')}>{L.sub}</a>
                      </div>
                      <div style={card}>
                        <div style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--color-text)' }}>{L.pr} <span style={{ color: 'var(--color-primary-dark)' }}>€19,90{L.yr}</span></div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>{L.prd}</div>
                        <a href={buildCheckoutUrl('premium', user)} target="_blank" rel="noopener noreferrer" style={btn('#111')}>{L.sub}</a>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 8 }}>🔒 {L.note}</div>
                  </div>
                );
              })()}
            </div>
          )}

          {a11yMenuOpen && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Sopra: AA x1.35 · AA x1.60 (tocco = ingrandisci, ritocco = normale) · Prova */}
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { lvl: 1, node: (<span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3 }}><span style={{ fontWeight: 800, letterSpacing: '-1px' }}>A<span style={{ fontSize: '1.25em' }}>A</span></span><span style={{ fontSize: '0.72rem' }}>x1.35</span></span>) },
                  { lvl: 2, node: (<span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3 }}><span style={{ fontWeight: 800, letterSpacing: '-1px' }}>A<span style={{ fontSize: '1.6em' }}>A</span></span><span style={{ fontSize: '0.72rem' }}>x1.60</span></span>) },
                ].map(({ lvl, node }) => (
                  <button key={lvl} type="button" onClick={() => setBigTo(bigLevel === lvl ? 0 : lvl)} aria-pressed={bigLevel === lvl}
                    style={{
                      ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: bigLevel === lvl ? '2px solid var(--color-primary)' : '2px solid var(--color-border)',
                      background: bigLevel === lvl ? 'var(--color-primary)' : 'var(--color-card)',
                      color: bigLevel === lvl ? '#fff' : 'var(--color-text)',
                    }}>{node}</button>
                ))}
                {/* Prova */}
                <button type="button" onClick={testA11yReminder} aria-busy={testing}
                  style={{
                    ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                    border: testing ? '2px solid var(--color-primary)' : '2px solid var(--color-border)',
                    background: testing ? 'var(--color-primary)' : 'var(--color-card)',
                    color: testing ? '#fff' : 'var(--color-text)',
                    transition: 'background 0.1s, border-color 0.1s',
                  }}>
                  <span className="tile-label">{testing ? '…' : (T.settings.voiceTest || 'Prova')}</span>
                </button>
              </div>
              {testResult && (
                <p role="status" style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: '8px 0 0', lineHeight: 1.4 }}>
                  {testResult}
                </p>
              )}

              {/* Sotto: Vibrazione · Lettura a voce · Alto contrasto (tocco = attiva/disattiva) */}
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { on: a11yVibrate,  toggle: toggleVibrate,  label: T.settings.a11yVibration },
                  { on: a11ySpeak,    toggle: toggleSpeak,    label: T.settings.a11ySpeak },
                  { on: a11yContrast, toggle: toggleContrast, label: T.settings.a11yContrast },
                ].map(({ on, toggle, label }, i) => (
                  <button key={i} type="button" onClick={toggle} aria-pressed={on}
                    style={{
                      ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                      border: on ? '2px solid var(--color-primary)' : '2px solid var(--color-border)',
                      background: on ? 'var(--color-primary)' : 'var(--color-card)',
                      color: on ? '#fff' : 'var(--color-text)',
                    }}>
                    <span className="tile-label">{label}</span>
                  </button>
                ))}
              </div>

              {/* Voce Lettura — ora vive SOLO qui dentro Accessibilità */}
              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 4 }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: 8 }}>{T.settings.voiceSection}</div>
                <VoicePicker lang={form.language} T={T} />
              </div>
            </div>
          )}


          {/* Connect Apple · Invite a friend · Support — the three things you
              reach for occasionally, on one line under the everyday ones. */}
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <button type="button" onClick={() => { setAppleMenuOpen(o => !o); setA11yMenuOpen(false); setUsageMenuOpen(false); setSupportMenuOpen(false); }} aria-expanded={appleMenuOpen}
              style={{ ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'var(--color-card)', color: 'var(--color-text)', border: appleMenuOpen ? '2px solid var(--color-primary)' : '2px solid var(--color-border)' }}>
              {/* Two lines, and no apple. Silvia tried the logo standing in for
                  the "o" and did not like it — "orrendo" — which is the only
                  verdict that counts on a thing whose whole job is to look
                  right. The word that names the thing goes on top, the brand
                  underneath, and nothing competes with either. */}
              <span className="tile-label" style={{ textAlign: 'center' }}>
                {({ it: 'Calendario', en: 'Calendar', es: 'Calendario', de: 'Kalender', fr: 'Calendrier', nl: 'Agenda', pt: 'Calendário', ru: 'Календарь', pl: 'Kalendarz', ro: 'Calendar', zh: '日历', ja: 'カレンダー', hi: 'कैलेंडर', ko: '캘린더', ar: 'تقويم' }[baseLang] || 'Calendar')}
                <br />Apple {appleMenuOpen ? '▲' : '▾'}
              </span>
            </button>
            <button type="button" onClick={() => { setInviteOpen(o => !o); setAppleMenuOpen(false); setSupportMenuOpen(false); setA11yMenuOpen(false); setUsageMenuOpen(false); }} aria-expanded={inviteOpen}
              style={{ ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'var(--color-card)', color: 'var(--color-text)', border: '2px solid var(--color-primary)' }}>
              <span className="tile-label" style={{ textAlign: 'center' }}>
                {INVITE_LABEL[baseLang] || INVITE_LABEL.en} {inviteOpen ? '▲' : '▾'}
              </span>
            </button>
            <button type="button" onClick={() => { setSupportMenuOpen(o => !o); setAppleMenuOpen(false); setA11yMenuOpen(false); setUsageMenuOpen(false); }} aria-expanded={supportMenuOpen}
              style={{ ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'var(--color-card)', color: 'var(--color-text)', border: supportMenuOpen ? '2px solid var(--color-primary)' : '2px solid var(--color-border)' }}>
              <span className="tile-label" style={{ textAlign: 'center' }}>{({ it: 'Supporto', en: 'Support', es: 'Soporte', de: 'Support', fr: 'Assistance', nl: 'Support', pt: 'Suporte', ru: 'Поддержка', pl: 'Wsparcie', ro: 'Suport', zh: '支持', ja: 'サポート', hi: 'सहायता', ko: '지원', ar: 'الدعم' }[baseLang] || 'Support')} {supportMenuOpen ? '▲' : '▾'}</span>
            </button>
          </div>

          {/* The message, and the ways of sending it. It used to copy in
              silence and answer "Copied" — which tells somebody that something
              happened, not what it was or where to put it. Now they read the
              words first and pick where they go. */}
          {inviteOpen && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: '0 0 8px' }}>
                {INVITE_HOW[baseLang] || INVITE_HOW.en}
              </p>
              <div style={{
                background: 'var(--color-card)', border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)', padding: '10px 12px',
                fontSize: '0.82rem', lineHeight: 1.5, color: 'var(--color-text)',
                whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 180, overflowY: 'auto',
              }}>
                {inviteText}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                {[
                  { key: 'wa', label: 'WhatsApp', href: `https://wa.me/?text=${encodeURIComponent(inviteText)}` },
                  { key: 'tg', label: 'Telegram', href: `https://t.me/share/url?url=${encodeURIComponent(WEB_URL)}&text=${encodeURIComponent(inviteText)}` },
                  { key: 'em', label: 'Email',    href: `mailto:?subject=${encodeURIComponent(INVITE_SUBJECT[baseLang] || INVITE_SUBJECT.en)}&body=${encodeURIComponent(inviteText)}` },
                ].map(o => (
                  <a key={o.key} href={o.href} target="_blank" rel="noopener noreferrer"
                    className="btn btn-outline btn-sm"
                    style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
                    {o.label}
                  </a>
                ))}
                <button type="button" onClick={copyInvite} className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap' }}>
                  {inviteCopied ? (INVITE_COPIED[baseLang] || INVITE_COPIED.en) : (INVITE_COPY[baseLang] || INVITE_COPY.en)}
                </button>
                {/* Offered only where the phone actually has a share sheet —
                    a button that opens nothing is worse than one less button. */}
                {typeof navigator !== 'undefined' && navigator.share && (
                  <button type="button" onClick={handleInvite} className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap' }}>
                    {INVITE_SHARE[baseLang] || INVITE_SHARE.en}
                  </button>
                )}
              </div>
            </div>
          )}
          {inviteError && (
            <p role="alert" style={{ fontSize: '0.78rem', color: '#c0392b', margin: '8px 0 0', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
              {inviteError}
            </p>
          )}


          {/* Collega Apple — solo l'abbonamento al Calendario Apple */}
          {appleMenuOpen && (<div style={{ marginTop: 12 }}><ExportSection T={T} lang={user?.language} mode="apple" /></div>)}
          {supportMenuOpen && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: '0 0 12px', lineHeight: 1.4 }}>{T.settings.supportDesc}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <a href="mailto:memoapp2026@gmail.com?subject=MeMo%20-%20Supporto" className="btn btn-outline btn-sm" style={{ textDecoration: 'none', display: 'inline-block', whiteSpace: 'nowrap' }}>✉️ {T.settings.emailSupport}</a>
                {botUsername && (
                  <a href={`https://t.me/${botUsername}?start=support`} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm" style={{ textDecoration: 'none', display: 'inline-block', whiteSpace: 'nowrap' }}>📨 {T.settings.contactSupport || 'Contact support on Telegram'}</a>
                )}
              </div>
            </div>
          )}

          {/* Accessibilità */}
        </div>

        <Divider />

        {/* No Save button: every setting on this page saves itself. What is
            left is the part that actually mattered — saying whether it worked.
            A quiet line that appears for a moment and goes, and a red one that
            stays until the next attempt succeeds. */}
        <div style={{ minHeight: 22, marginTop: 12, marginBottom: 8 }} aria-live="polite">
          {saving && (
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{T.settings.saving}</span>
          )}
          {!saving && saved && (
            <span style={{ fontSize: '0.78rem', color: 'var(--color-primary-dark)', fontWeight: 700 }}>
              {T.settings.saved}
            </span>
          )}
        </div>
        {saveError && (
          <div className="alert alert-error" style={{ marginTop: 4, marginBottom: 12 }}>
            {saveErrorMsg || T.settings.saveError}
          </div>
        )}
      </form>

      {/* ── Fondo: .ics · Elimina account ── */}
      <Divider />
      <div style={{ padding: '18px 0 8px' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          {/* A copy of your events to keep */}
          <button type="button" onClick={() => { setIcsMenuOpen(o => !o); setDeleteMenuOpen(false); }} aria-expanded={icsMenuOpen}
            style={{ ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'var(--color-card)', color: 'var(--color-text)', border: icsMenuOpen ? '2px solid var(--color-primary)' : '2px solid var(--color-border)' }}>
            <span className="tile-label" style={{ textAlign: 'center' }}>.ics {icsMenuOpen ? '▲' : '▾'}</span>
          </button>
          {/* Deleting the account. It keeps its red, and it is the only thing
              beside it now — the one action on this page that cannot be undone
              no longer sits a thumb's width from Support. */}
          <button type="button" onClick={() => { setDeleteMenuOpen(o => !o); setIcsMenuOpen(false); }} aria-expanded={deleteMenuOpen}
            style={{ ...themeButtonBase, flex: 1, minWidth: 0, minHeight: 56, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'var(--color-card)', color: '#c0392b', border: deleteMenuOpen ? '2px solid #c0392b' : '2px solid #e6b0aa' }}>
            <span className="tile-label" style={{ textAlign: 'center' }}>{dl.btn} {deleteMenuOpen ? '▲' : '▾'}</span>
          </button>
        </div>

        {icsMenuOpen && (<div style={{ marginTop: 12 }}><ExportSection T={T} lang={user?.language} mode="ics" /></div>)}


        {deleteMenuOpen && (
          <div style={{ marginTop: 12 }}>
            <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: '0 0 10px', lineHeight: 1.45 }}>{dl.desc}</p>
            <button type="button" onClick={handleDeleteAccount} disabled={deletingAccount}
              style={{ background: 'transparent', color: '#c0392b', border: '1.5px solid #c0392b', borderRadius: 10, padding: '7px 14px', fontWeight: 700, fontSize: '0.82rem', cursor: deletingAccount ? 'default' : 'pointer', opacity: deletingAccount ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <TrashIcon size={15} /> {deletingAccount ? dl.deleting : dl.btn}
            </button>
            {deleteAccountErr && (<p style={{ marginTop: 8, fontSize: '0.78rem', color: '#e63946' }}>{deleteAccountErr}</p>)}
          </div>
        )}
      </div>

      {/* Build version — updates by itself on each deploy (see scripts/genBuildInfo.js).
          Also the hidden way in to the speed measurement: five taps. Left as
          plain text on purpose, not a button — a screen reader announcing
          "Version updated on 17/08/2026, button" would be a puzzle offered to
          the one person least able to solve it, for a door they have no reason
          to open. */}
      <div onClick={tapVersion}
        style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--color-text-muted)', opacity: 0.7, padding: '18px 0 8px' }}>
        {(() => {
          const [y, m, d] = (BUILD_DATE || '').split('-');
          const shown = (y && m && d) ? `${d}/${m}/${y}` : BUILD_DATE;
          const L = { it: 'Versione aggiornata il', en: 'Version updated on', es: 'Versión actualizada el', de: 'Version aktualisiert am', fr: 'Version mise à jour le', nl: 'Versie bijgewerkt op', pt: 'Versão atualizada em', ru: 'Версия обновлена', pl: 'Wersja zaktualizowana', ro: 'Versiune actualizată la', zh: '版本更新于', ja: 'バージョン更新日', hi: 'संस्करण अपडेट', ko: '버전 업데이트', ar: 'تاريخ تحديث الإصدار' }[baseLang] || 'Version updated on';
          // The minute is the point. MeMo was deployed fourteen times on 20
          // August, and a date alone cannot tell one of those from another —
          // Silvia spent an evening certain she had an update she did not
          // have, with nothing on the screen able to contradict her.
          const stamp = [shown, BUILD_TIME, BUILD_COMMIT].filter(Boolean).join(' · ');
          return `MeMo · ${L} ${stamp}`;
        })()}
      </div>

      {tooWide && (
        <div style={{
          margin: '0 0 12px', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
          border: '1.5px solid #e6b0aa', background: '#fdf3f2',
          fontSize: '0.7rem', lineHeight: 1.45, color: '#8a3a30', overflowWrap: 'anywhere',
        }}>
          <b>Questo schermo è più stretto della pagina.</b><br />
          {tooWide}
        </div>
      )}

      {/* Reachable, not hidden. The five-tap diagnostics below are for us; this
          is for anybody holding the installed app, which has no other way to
          ask for the current version of itself. */}
      <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 14 }}>
        <button type="button" onClick={reloadApp} disabled={reloading}
          className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap' }}>
          {reloading ? '…' : (RELOAD_LABEL[baseLang] || RELOAD_LABEL.en)}
        </button>
      </div>

      {/* The speed measurement — hidden, not gone. Appears after five taps on
          the version line above. Kept out of sight for the testers, kept
          reachable for us: after launch, with real users, these are the numbers
          that say whether the waiting is worth doing something about.
          To remove the instrument altogether: the list is at the bottom of
          utils/perfLog.js. */}
      {showDiag && (
        <div style={{ textAlign: 'center', padding: '0 0 20px' }}>
          <Link to="/velocita" style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', opacity: 0.6 }}>
            {{ it: 'Velocità (diagnostica)', es: 'Velocidad (diagnóstico)', fr: 'Vitesse (diagnostic)',
               de: 'Geschwindigkeit (Diagnose)', pt: 'Velocidade (diagnóstico)' }[baseLang] || 'Speed (diagnostics)'}
          </Link>
        </div>
      )}

    </div>
  );
}
